import type { DossierThread } from './threadContracts';
import type {RelationsInput,RelationsResult} from './relationsContracts';
import { getAuthContextState as faithfulAuth } from '@/uptk/core/api/auth_context_store';
import { getTenantOverrideState as faithfulTenant } from '@/uptk/core/api/tenant_override_store';
import { createMemoryFaithfulRepository } from '@/uptk/core/components/moduleEntity/faithful/repositories';
import { gapReport, type GapReport } from '../catalog/gapReport';
import type {ZoneCodeResult} from '../designer/zoneExtensions';
import { fidelityLedger } from '../catalog/fidelityLedger';
import { assistedBindings, type BindingCandidate } from '../designer/assistedBindings';
import type { ModuleSpecBlock as BindingBlock } from './moduleSpecContracts';
import type { TranslationPreference, StarterTestPlanResult } from './cleanModelContracts';
import type { ModuleComponents } from './contracts';
import moduleComponentExample from '../components/geode-components.example.json';
import computedPilot from './fixtures/finances-personnelles.v1.4.module-spec.json';
import type { ScreenAnalysisInput, ScreenAnalysis } from './screenAnalysisContracts';
import type { CapabilityCatalog, CoverageReport, NavigationProposal } from './contracts';
import type { ReferenceCapture, ReferenceCaptureInput } from './contracts';
import { CaptureFixture } from '../prototypes/captureFixture';
import capabilityCatalog from '../catalog/capability-catalog.v1.json';
import { inventoryCoverage, modelCoverage } from '../catalog/coverageModel';
import { suggestNavIcon } from '../catalog/navIconSuggest';
import type { Simulation, SimulationSummary, SimulationCreateInput, SimulationUpdateInput, SimulationGenerateInput, SimulationRefreshInput, SimulationRefreshResult, SimulationAcknowledgeInput, SimulationShareInput, SimulationShareCreated } from './contracts';
import { IdeaFixture } from '../idea/ideaFixture';
import {supportedSpecVersion} from './specVersion';
/**
 * Module Studio (UPT-2079 lots L0 à L2, UPT-2095) — dépôt de démonstration : les jeux
 * JSON du contrat (dossiers, specs, plan « Recette Quenotte pilote »,
 * exécution), chargés par import statique (aucune requête réseau).
 *
 * Écritures : en mémoire, persistées dans `localStorage` sous la clé
 * `moduleStudio.fixtures.v1`. CHAQUE accès au stockage est dans un try/catch
 * (navigation privée, stockage plein) : en cas d'échec, la session continue en
 * mémoire seulement. Latence simulée nulle.
 *
 * L2 (web §4.3) : les règles des dossiers et des specs sont celles du serveur
 * (rest §4.3 à §4.7, mêmes codes), levées sous la forme
 * `{ response: { status, data: { code, message } } }` pour passer par
 * `readDesignError` sans distinction ; versions immuables sous
 * `specVersions` ; diff par `unifiedDiff` ; squelette par `buildSpecSkeleton` ;
 * synchronisation toujours indisponible (`no-config` : pas de dépôt git).
 *
 * Les compteurs (specCount, testPlanCount, openTestRuns, lastRun, done /
 * failed / proposed des résumés) sont CALCULÉS depuis les données, jamais lus
 * tels quels.
 *
 * UPT-2079 L3 (web §4.3) : brouillon du Concepteur sous `moduleSpecDrafts` (un par dossier en démonstration,
 * auteur = `currentUserName`) : règles de `PUT …/draft` (forme, bornes `DESIGNER_LIMITS`, `layout`, `baseChecksum`),
 * copie d'une version, proposition (règles `propose` puis suppression du brouillon), export du seul module « installé »
 * `pret-materiel`, catalogue de démonstration (`designer/catalogDefaults.ts`). Checksum par `designer/specChecksum.ts`
 * (extrait d'ici : aucune duplication).
 * UPT-2079 L4 (web §4.3) : jeux de données d'aperçu sous `previewDatasets` (par dossier, 20 au plus) : règles du serveur
 * reproduites (rest §4.3 à §4.8, mêmes codes) ; instantané par `preview/previewSnapshot.ts`, lignes validées par
 * `preview/previewRows.ts`, générées par `preview/previewRowGenerator.ts` ; avertissements `pii.*` par `scanPersonalData`
 * (importé ICI seulement : `preview/` n'importe jamais `prototypes/warnings`, garde `gate:analysis`) ; empreinte =
 * `checksumOf` du JSON canonique ; acquittement par empreinte ; AUCUN lien de partage (`NotServedError('preview', …)`).
 * UPT-2079 L5 (web §4.3) : exécution d'exemple du générateur sous `generationRuns` (par dossier) : UNE exécution `succeeded` pour le
 * dossier de démonstration (`fixtures/generator-run.pret-materiel.json`, plan de référence `pret-materiel`, 26 fichiers depuis le générateur v2 de L5e : cycle de vie et adaptateurs de statut) ; elle se
 * lit (`listGenerationRuns`, `getGenerationRun`), tout le reste lève `NotServedError('generator' | 'generatorStripe' | 'generatorRepo', …)` : le
 * serveur seul sait produire le bundle, la démonstration est en lecture seule (aucune suppression non plus). UPT-2079 L5d (web §11.4) :
 * l'exécution d'exemple porte `repo: null` et `repoStatus: null` (aucun dépôt dans git en démonstration, aucun bloc à l'écran).
 * UPT-2079 L6a (web §4.3) : pipeline sous `fixtures/pipeline.json` (quatre environnements FIGÉS, `current: 'DEV'`, un relevé CODE et une
 * promotion DATA d'exemple, deux locataires fictifs) ; il se lit (`getPipelineEnvironments`, `listPipelineRuns`, `getPipelineRun`,
 * `listPipelineTenants`), toute écriture lève `NotServedError('pipeline', …)` : la démonstration ne relève, ne modifie et ne supprime rien.
 * UPT-2079 L10a (web §4.3) : retours de recette sous `fixtures/feedback.json` (sept retours FIGÉS du dossier de démonstration `dz-module-studio`, module
 * `pret-materiel` : un à classer, un à transmettre, quatre acheminés — un par catégorie de défaut —, un résolu) ; ils se lisent (`listFeedback` filtre côté
 * client et calcule `counts`, `getFeedback` rend le détail avec son journal), les huit autres méthodes lèvent `NotServedError('feedback', …)` : la
 * démonstration ne saisit, ne modifie, ne classe, ne transmet, ne résout, n'écarte et ne rouvre rien.
 * UPT-2095 (web §11.2) : versions de Module Spec sous `moduleSpecVersions`
 * (par dossier) ; jeu initial pour `dz-module-studio` = `pret-materiel` v1
 * `accepted` (`human`) et v2 `proposed` (`mcp`, « Claude via MCP », deux
 * erreurs et trois avertissements) ; règles du serveur reproduites (`propose` :
 * contrôles L2, dédoublonnage par checksum, version suivante `proposed`,
 * rapport par `moduleSpecLocalCheck` ; `decide` : `not_proposed`, `has_errors`,
 * `comment_required`, `superseded` ; projection `moduleSpecVersion` /
 * `moduleSpecStatus` recalculée sur le dossier) ; diff par `moduleSpecLocalDiff`.
 */
import {
  DESIGN_LIMITS,
  FEEDBACK_CATEGORIES,
  FEEDBACK_LIMITS,
  FEEDBACK_STATUSES,
  PREVIEW_LIMITS,
  PIPELINE_LIMITS,
  DESIGN_SPEC_KINDS,
  DESIGN_SPEC_STATUSES,
  DOSSIER_STAGES,
  PROTOTYPE_ACK_REQUIRED_CODES,
  PROTOTYPE_INVENTORY_LIMITS,
  PROTOTYPE_LIMITS,
  PROTOTYPE_STYLE_LIMITS,
  PROTOTYPE_RIGHTS_TERMS_VERSION,
  type DeliveryCheck,
  type DesignDossier,
  type DesignDossierInput,
  type DesignModuleSpecInput,
  type DesignModuleSpecPayload,
  type DesignSpecDetail,
  type DesignSpecDiff,
  type DesignSpecDocument,
  type DesignSpecDocumentInput,
  type DesignSpecGenerateInput,
  type DesignSpecKind,
  type DesignSpecStatus,
  type DesignSpecSyncInput,
  type DesignSpecSyncResult,
  type DesignSpecVersion,
  type DesignSpecVersionSource,
  type DesignSpecVersionSummary,
  type DossierStatus,
  type EnvironmentCode,
  type Feedback,
  type FeedbackCounts,
  type FeedbackFilter,
  type FeedbackPage,
  type FeedbackSummary,
  type FeedbackTransmission,
  type GeneratorRepoDeposit,
  type GeneratorRun,
  type GeneratorRunSummary,
  type GeneratorStripeResult,
  type PipelineEnvironment,
  type PipelineEnvironmentInput,
  type PipelineEnvironmentsPayload,
  type PipelineGateTests,
  type PipelineJourney,
  type PipelineKind,
  type PipelineLicenseResult,
  type PipelineRun,
  type PipelineRunSummary,
  type PipelineTenantOption,
  type AiRoute,
  type PrototypeAcknowledgeInput,
  type PrototypeAnalysis,
  type PrototypeAnalysisApplyInput,
  type PrototypeAnalysisApplyResult,
  type PrototypeAnalysisEstimate,
  type PrototypeAnalysisInput,
  type PrototypeAnalysisRejectInput,
  type PrototypeAnalysisSummary,
  type PrototypeDerivationDeclaration,
  type PrototypeImageInput,
  type PrototypeImport,
  type PrototypeImportInput,
  type PrototypeImportPatch,
  type PrototypeImportSummary,
  type PrototypeInventory,
  type PrototypePiece,
  type PrototypePrompt,
  type PrototypeScreen,
  type PrototypeSnapshotInput,
  type PrototypeSourceInput,
  type PrototypeSnapshotResult,
  type PrototypeWarning,
  type PreviewAcknowledgeInput,
  type PreviewDataset,
  type PreviewDatasetCreateInput,
  type PreviewDatasetSummary,
  type PreviewDatasetUpdateInput,
  type PreviewGenerateInput,
  type PreviewRefreshInput,
  type PreviewRefreshResult,
  type PreviewRow,
  type PreviewShareCreated,
  type PreviewShareInput,
  type PreviewSnapshot,
  type PreviewSource,
  type PreviewWarning,
  type PreviewWarningCode,
  type PreviewWarningSource,
  type SpecDocument,
  type TestPlan,
  type TestPlanSummary,
  type TestResult,
  type TestResultInput,
  type TestRun,
  type TestRunSummary,
} from './contracts';
import type { PreparedHtml, PreparedImage, PrototypeUploadFiles } from '../prototypes/files';
import { scanForm, scanPersonalData } from '../prototypes/warnings';
import { designError } from '../specs/designErrors';
import { isSpecContentTooLarge, lineCount, normalizeSpecContent, utf8Bytes } from '../specs/specContent';
import { isSpecPath } from '../specs/specPath';
import { buildSpecSkeleton, formatSkeletonDate, type SkeletonPrototype } from '../specs/specSkeleton';
import { unifiedDiff } from '../specs/unifiedDiff';
import { NotServedError, type ModuleStudioRepository } from './ModuleStudioRepository';
import type {
  ModuleSpecDecisionInput,
  ModuleSpecDiffSection,
  ModuleSpecDocument,
  ModuleSpecProposeInput,
  ModuleSpecRepairPreview,
  ModuleSpecVersion,
  ModuleSpecVersionSummary,
  ValidationReport,
} from './moduleSpecContracts';
import type {
  ModuleAsset,
  ModuleAssetFromPieceInput,
  ModuleAssetInput,
  PrototypePresentationResponse,
} from './contracts';
import { DemoAssetStore, blobToBytes } from './fixtureAssets';
import { MODULE_SPEC_SECTION_SELECTOR } from './moduleSpecContracts';
import { presentationFromSummary, resolvePresentation } from '../presentation/presentationModel';
import type { ModuleSpecTranslationEstimate, ModuleSpecTranslationJob } from './contracts';
import { DemoTranslationJobs, type DemoTranslationHost } from './fixtureTranslationJobs';
import { isModuleStudioDemoProfile } from './capabilities';
import { translateDemoLabels } from '../modulespec/translationModel';
import { moduleSpecLocalCheck } from '../modulespec/moduleSpecLocalCheck';
import { moduleSpecLocalDeliveryCheck } from '../modulespec/moduleSpecLocalDelivery';
import { moduleSpecLocalRepair } from '../modulespec/moduleSpecLocalRepair';
import { generateRows } from '../preview/previewRowGenerator';
import { validateRows, type PreviewRowError } from '../preview/previewRows';
import { buildPreviewSnapshot, isPreviewSnapshotTooLarge } from '../preview/previewSnapshot';
import { DEMO_CATALOG } from '../designer/catalogDefaults';
import { canonicalJson, checksumOf, fnv1a64Hex, moduleSpecChecksum } from '../designer/specChecksum';
import { moduleSpecLocalDiff } from '../modulespec/moduleSpecLocalDiff';
import {
  DESIGNER_LIMITS,
  type DesignerCatalog,
  type DesignerNodePosition,
  type ModuleSpecDraft,
  type ModuleSpecDraftInput,
  type ModuleSpecDraftLayout,
  type ModuleSpecDraftProposeInput,
  type ModuleSpecExportPreview,
  type ModuleSpecExportResult,
} from './contracts';
import dossiersFixture from './fixtures/dossiers.json';
import generatorRunFixture from './fixtures/generator-run.pret-materiel.json';
import pretMaterielFixture from './fixtures/pret-materiel.module-spec.json';
import moduleSpecV2Fixture from './fixtures/moduleSpec.v2.json';
import feedbackFixture from './fixtures/feedback.json';
import pipelineFixture from './fixtures/pipeline.json';
import specsFixture from './fixtures/specs.json';
import testPlanFixture from './fixtures/testplan.quenotte-pilote.json';
import testRunsFixture from './fixtures/testruns.json';

export const FIXTURE_STORAGE_KEY = 'moduleStudio.fixtures.v1';

type FixtureState = {
  dossiers: DesignDossier[];
  specs: SpecDocument[];
  /** L2 : versions immuables par identifiant de document (ordre croissant). */
  specVersions: Record<string, DesignSpecVersion[]>;
  plans: TestPlan[];
  runs: TestRun[];
  /** UPT-2095 : versions de Module Spec par dossier (ordre croissant de `version`). */
  moduleSpecVersions: Record<string, ModuleSpecVersion[]>;
  /** UPT-2079 L3 : brouillon du Concepteur par dossier (un seul auteur en démonstration). */
  moduleSpecDrafts: Record<string, ModuleSpecDraft>;
  /** UPT-2079 L4 : jeux de données d'aperçu par dossier (20 au plus, du plus ancien au plus récent). */
  previewDatasets: Record<string, PreviewStoredDataset[]>;
  /** UPT-2079 L5 : exécutions du générateur par dossier (en démonstration : l'exécution d'exemple seulement, lecture seule). */
  generationRuns: Record<string, GeneratorRun[]>;
};

/** Jeu de démonstration : le `PreviewDataset` rendu + l'acquittement conservé (valable tant que l'empreinte est la même). */
type PreviewStoredDataset = PreviewDataset & { ack: { sha: string; by: string; at: string } | null };

/** Entrée du jeu `specs.json` (L0) : les champs L2 peuvent manquer. */
type RawSpecDocument = Partial<DesignSpecDocument> &
  Pick<DesignSpecDocument, 'id' | 'dossierId' | 'kind' | 'title' | 'path' | 'status' | 'currentVersion' | 'updatedAt'>;

/** Applique aux entrées de `specs.json` les champs L2 absents (les JSON de démonstration ne sont pas modifiés). */
export const normalizeSpec = (raw: RawSpecDocument): DesignSpecDocument => ({
  id: raw.id,
  dossierId: raw.dossierId,
  kind: raw.kind,
  title: raw.title,
  path: raw.path,
  status: raw.status,
  assignee: raw.assignee ?? null,
  ticketRef: raw.ticketRef ?? null,
  currentVersion: raw.currentVersion,
  currentVersionId: raw.currentVersionId ?? null,
  checksum: raw.checksum ?? null,
  lastSource: raw.lastSource ?? null,
  repoCommit: raw.repoCommit ?? null,
  syncedAt: raw.syncedAt ?? null,
  createdAt: raw.createdAt ?? raw.updatedAt,
  updatedAt: raw.updatedAt,
});

export type FixtureRepositoryOptions = {
  /** Nom de l'utilisateur courant (validation d'une proposition IA). */
  currentUserName?: () => string;
  /** UPT-2096 : organisation affichée des imports de démonstration (i18n par l'appelant). */
  demoOrganisation?: () => string;
  /** UPT-2096 : identifiants injectés dans les tests (défaut crypto.randomUUID()). */
  newId?: () => string;
};

/** Import de démonstration : record + contenus en mémoire (jamais persistés, §3.5). */
type DemoPrototypeImport = {
  record: PrototypeImport;
  prompt: string | null;
  texts: Map<string, string>;
  images: Map<string, Blob>;
};

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

/** Taille par défaut d'une liste d'exécutions du pipeline (le serveur : `PIPELINE_LIMITS.listDefault`). */
const PIPELINE_LIST_DEFAULT = PIPELINE_LIMITS.listDefault;

/* ── UPT-2095 : versions de Module Spec de démonstration ──────────────────── */

/** Dossier de démonstration qui porte les deux versions du jeu initial. */
export const DEMO_MODULE_SPEC_DOSSIER = 'dz-module-studio';
const MODULE_SPEC_COMMENT_MAX = 2000;
/** UPT-2079 L3 : seul module « installé » en démonstration (export). */
export const DEMO_EXPORT_MODULE = 'pret-materiel';
const DEMO_EXPORT_VERSION = '1.0.0';

/** Remarques « corrigé automatiquement » de la réparation locale (messages « fr — en » du validateur), par nature de correction. */
const AUTOFIX_MESSAGES: Readonly<Record<string, string>> = {
  'reference.identifier': 'clé de référence remplacée par id — reference key replaced with id',
  'page.kind_other_migrated': 'corrigé automatiquement : page obsolète convertie en écran sur mesure — automatically fixed: obsolete page converted to a custom screen',
  'workflow.state_key': "corrigé automatiquement : identifiant d'état de workflow renommé — automatically fixed: workflow state key renamed",
  'page.description': "corrigé automatiquement : description d'écran complétée — automatically fixed: screen description completed",
  'module.domain': 'corrigé automatiquement : domaine du module ramené au bon format — automatically fixed: module domain put in the right format',
  'relation.duplicate': 'corrigé automatiquement : lien en double traité — automatically fixed: duplicate link handled',
  // UPT-2079 L12a : le socle d'abord et les noms déjà pris.
  'core.entity_removed': 'corrigé automatiquement : objet qui doublait le socle retiré — automatically fixed: object that duplicated the platform removed',
  'core.field_retyped': 'corrigé automatiquement : champ devenu « pièces jointes » — automatically fixed: field became “attachments”',
  'core.field_removed': 'corrigé automatiquement : champ remplacé par le socle retiré — automatically fixed: field replaced by the platform removed',
  'core.relation_removed': 'corrigé automatiquement : lien vers le socle retiré — automatically fixed: link to the platform removed',
  'core.reuse_declared': 'corrigé automatiquement : capacité du socle déclarée — automatically fixed: platform capability declared',
  'core.reference_cleaned': 'corrigé automatiquement : référence à un objet retiré nettoyée — automatically fixed: reference to a removed object cleaned',
  'entity.renamed': 'corrigé automatiquement : objet renommé, son nom était déjà pris — automatically fixed: object renamed, its name was already taken',
  'entity.slugs_set': "corrigé automatiquement : adresse d'objet précisée — automatically fixed: object address made explicit",
  'workflow.transition_resolved': "corrigé automatiquement : transition retrouvée par le nom de l'état — automatically fixed: transition matched by state name",
  // Relecture rest du 18.10 : G4, R3, G3.
  'workflow.state_key_duplicate': "corrigé automatiquement : état de workflow en double, clé suffixée — automatically fixed: duplicate workflow state, key suffixed",
  'workflow.dropped': 'corrigé automatiquement : cycle de vie retiré (moins de deux états lisibles) — automatically fixed: life cycle dropped (fewer than two readable states)',
  'core.candidate_kept': 'corrigé automatiquement : objet conservé bien qu\'il ressemble au socle — automatically fixed: object kept although it resembles the platform',
};

/** Projection du dossier (rest §11.1) : dernière `accepted`, sinon dernière `proposed`, sinon aucune. */
const projectionOf = (versions: ModuleSpecVersion[]): { version: number; status: 'proposed' | 'accepted' | null; spec: ModuleSpecDocument | null } => {
  const accepted = [...versions].reverse().find((entry) => entry.status === 'accepted');
  const proposed = [...versions].reverse().find((entry) => entry.status === 'proposed');
  const current = accepted ?? proposed ?? null;
  return current
    ? { version: current.version, status: current.status === 'accepted' ? 'accepted' : 'proposed', spec: current.spec }
    : { version: 0, status: null, spec: null };
};

const toModuleSpecSummary = (entry: ModuleSpecVersion): ModuleSpecVersionSummary => ({
  dossierRef: entry.dossierRef,
  version: entry.version,
  specVersion: entry.specVersion,
  moduleCode: entry.moduleCode,
  source: entry.source,
  status: entry.status,
  errorCount: entry.errorCount,
  warningCount: entry.warningCount,
  coverage: entry.coverage,
  authoredBy: entry.authoredBy,
  createdAt: entry.createdAt,
  decidedBy: entry.decidedBy,
  decidedAt: entry.decidedAt,
  prototypeImportId: entry.prototypeImportId ?? null,
});

const demoVersion = (
  spec: ModuleSpecDocument,
  fields: Pick<ModuleSpecVersion, 'version' | 'source' | 'status' | 'authoredBy' | 'createdAt' | 'decidedBy' | 'decidedAt' | 'decisionComment'>
): ModuleSpecVersion => {
  const validation = moduleSpecLocalCheck(spec);
  return {
    dossierRef: DEMO_MODULE_SPEC_DOSSIER,
    specVersion: spec.specVersion,
    moduleCode: typeof spec.module.code === 'string' ? spec.module.code : null,
    errorCount: validation.errors.length,
    warningCount: validation.warnings.length,
    coverage: validation.scores.coverage,
    prototypeImportId: null,
    spec,
    checksum: fnv1a64Hex(new TextEncoder().encode(canonicalJson(spec))),
    validation,
    ...fields,
  };
};

/** Jeu initial : v1 acceptée (humain, rapport sans erreur, couverture 100) et v2 proposée par un agent MCP. */
const seedModuleSpecVersions = (): Record<string, ModuleSpecVersion[]> => ({
  [DEMO_MODULE_SPEC_DOSSIER]: [
    demoVersion(clone(pretMaterielFixture) as ModuleSpecDocument, {
      version: 1,
      source: 'human',
      status: 'accepted',
      authoredBy: 'Olivier',
      createdAt: '2026-10-11T09:00:00+02:00',
      decidedBy: 'Olivier',
      decidedAt: '2026-10-11T09:05:00+02:00',
      decisionComment: null,
    }),
    demoVersion(clone(moduleSpecV2Fixture) as ModuleSpecDocument, {
      version: 2,
      source: 'mcp',
      status: 'proposed',
      authoredBy: 'Claude via MCP',
      createdAt: '2026-10-13T09:15:00+02:00',
      decidedBy: null,
      decidedAt: null,
      decisionComment: null,
    }),
  ],
});

/** Applique la projection des versions aux dossiers (jeu initial et stockage antérieur à UPT-2095). */
const applyProjections = (state: FixtureState): FixtureState => {
  state.dossiers.forEach((dossier) => {
    const versions = state.moduleSpecVersions[dossier.id];
    if (versions && versions.length > 0) {
      const projection = projectionOf(versions);
      dossier.moduleSpecVersion = projection.version;
      dossier.moduleSpecStatus = projection.status;
    }
  });
  return state;
};

/** UPT-2079 L5 : l'exécution d'exemple (plan de référence `pret-materiel`) rattachée au dossier de démonstration. */
const seedGenerationRuns = (): Record<string, GeneratorRun[]> => ({
  [DEMO_MODULE_SPEC_DOSSIER]: [clone(generatorRunFixture) as unknown as GeneratorRun],
});

const seedState = (): FixtureState =>
  applyProjections({
    dossiers: clone(dossiersFixture) as DesignDossier[],
    specs: (clone(specsFixture) as RawSpecDocument[]).map(normalizeSpec),
    specVersions: {},
    plans: [clone(testPlanFixture) as TestPlan],
    runs: clone(testRunsFixture) as TestRun[],
    moduleSpecVersions: seedModuleSpecVersions(),
    moduleSpecDrafts: {},
    previewDatasets: {},
    generationRuns: seedGenerationRuns(),
  });

const nowIso = (): string => new Date().toISOString();

const randomSuffix = (): string => Math.random().toString(36).slice(2, 8);

/** done : étapes vérifiées (pass, fail, blocked) — ni « proposed » ni « skipped ». */
const isDoneVerdict = (verdict: string): boolean =>
  verdict === 'pass' || verdict === 'fail' || verdict === 'blocked';

const readStorage = (): FixtureState | null => {
  try {
    const raw = window.localStorage.getItem(FIXTURE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FixtureState>;
    if (!parsed || !Array.isArray(parsed.dossiers) || !Array.isArray(parsed.plans)) return null;
    // Un stockage antérieur à L2 n'a ni `specVersions` ni les champs L2 des specs : tolérés ;
    // antérieur à UPT-2095 : sans `moduleSpecVersions` → jeu initial des versions (projection recalculée).
    return applyProjections({
      dossiers: parsed.dossiers,
      specs: (Array.isArray(parsed.specs) ? (parsed.specs as RawSpecDocument[]) : []).map(normalizeSpec),
      specVersions: parsed.specVersions ?? {},
      plans: parsed.plans,
      runs: Array.isArray(parsed.runs) ? parsed.runs : [],
      moduleSpecVersions: parsed.moduleSpecVersions ?? seedModuleSpecVersions(),
      // antérieur à UPT-2079 L3 : sans `moduleSpecDrafts` → aucun brouillon.
      moduleSpecDrafts: parsed.moduleSpecDrafts ?? {},
      // antérieur à UPT-2079 L4 : sans `previewDatasets` → aucun jeu.
      previewDatasets: parsed.previewDatasets ?? {},
      // antérieur à UPT-2079 L5 : sans `generationRuns` → l'exécution d'exemple.
      generationRuns: parsed.generationRuns ?? seedGenerationRuns(),
    });
  } catch {
    return null;
  }
};

const writeStorage = (state: FixtureState): void => {
  try {
    window.localStorage.setItem(FIXTURE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Stockage indisponible ou plein : la session continue en mémoire seulement.
  }
};

const clearStorage = (): void => {
  try {
    window.localStorage.removeItem(FIXTURE_STORAGE_KEY);
  } catch {
    // Même repli : rien à effacer.
  }
};

/* ── Règles des dossiers (rest §4.3) et des specs (rest §4.4.2) ─────────── */

const DOSSIER_CODE_PATTERN = new RegExp(DESIGN_LIMITS.dossierCodePattern);
const MODULE_CODE_PATTERN = new RegExp(DESIGN_LIMITS.moduleCodePattern);
const TICKET_PATTERN = new RegExp(DESIGN_LIMITS.ticketRefPattern);
const ALL_DOSSIER_STATUSES: readonly DossierStatus[] = [...DOSSIER_STAGES, 'archived'];
const KIND_ORDER: readonly DesignSpecKind[] = DESIGN_SPEC_KINDS;

const SKELETON_MESSAGE = 'Squelette du gabarit (sans IA)';

type OptionalRef = string | null | undefined;

/** `absent = inchangé, null = effacé` ; trim, 1..64 sinon 400 (champ nommé). */
const normalizeRef = (value: OptionalRef, field: string): OptionalRef => {
  if (value === undefined || value === null) return value;
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > DESIGN_LIMITS.refMaxChars) {
    throw designError('dossier.invalid', field);
  }
  return trimmed;
};

const normalizeTicketRefs = (refs: string[] | null | undefined): string[] => {
  const result: string[] = [];
  for (const raw of refs ?? []) {
    const ticket = String(raw ?? '').trim().toUpperCase();
    if (!TICKET_PATTERN.test(ticket)) {
      throw designError('dossier.invalid', 'ticketRefs');
    }
    if (!result.includes(ticket)) result.push(ticket);
  }
  if (result.length > DESIGN_LIMITS.ticketRefsMax) {
    throw designError('dossier.invalid', 'ticketRefs');
  }
  return result;
};

const normalizeModuleCode = (value: string | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  const code = value.trim().toLowerCase();
  if (code.length === 0) return null;
  if (!MODULE_CODE_PATTERN.test(code)) {
    throw designError('dossier.invalid', 'moduleCode');
  }
  return code;
};

const normalizeDossierName = (value: string): string => {
  const name = (value ?? '').trim();
  if (name.length === 0 || name.length > DESIGN_LIMITS.dossierNameMaxChars) {
    throw designError('dossier.invalid', 'name');
  }
  return name;
};

const normalizeSummary = (value: string | null | undefined): string => {
  const summary = value ?? '';
  if (summary.length > DESIGN_LIMITS.dossierSummaryMaxChars) {
    throw designError('dossier.invalid', 'summary');
  }
  return summary;
};

const normalizeDossierStatus = (value: DossierStatus | undefined): DossierStatus => {
  const status = value ?? 'idea';
  if (!ALL_DOSSIER_STATUSES.includes(status)) {
    throw designError('dossier.invalid', 'status');
  }
  return status;
};

type NormalizedSpecInput = {
  kind: DesignSpecKind;
  title: string;
  path: string;
  status: DesignSpecStatus;
  assignee: string | null;
  ticketRef: string | null;
  message: string | null;
  /** undefined = métadonnées seules. */
  contentMd: string | undefined;
};

const normalizeSpecInput = (input: DesignSpecDocumentInput): NormalizedSpecInput => {
  if (!KIND_ORDER.includes(input.kind)) throw designError('spec.invalid', 'kind');
  const title = (input.title ?? '').trim();
  if (title.length === 0 || title.length > DESIGN_LIMITS.specTitleMaxChars) {
    throw designError('spec.invalid', 'title');
  }
  const path = (input.path ?? '').trim();
  if (!isSpecPath(path)) throw designError('spec.invalid', 'path');
  const status = input.status ?? 'libre';
  if (!DESIGN_SPEC_STATUSES.includes(status)) throw designError('spec.invalid', 'status');
  const assigneeRaw = (input.assignee ?? '').trim();
  if (assigneeRaw.length > DESIGN_LIMITS.specAssigneeMaxChars) throw designError('spec.invalid', 'assignee');
  const ticketRaw = (input.ticketRef ?? '').trim().toUpperCase();
  if (ticketRaw.length > 0 && !TICKET_PATTERN.test(ticketRaw)) throw designError('spec.invalid', 'ticketRef');
  const messageRaw = (input.message ?? '').trim();
  if (messageRaw.length > DESIGN_LIMITS.specMessageMaxChars) throw designError('spec.invalid', 'message');
  return {
    kind: input.kind,
    title,
    path,
    status,
    assignee: assigneeRaw.length > 0 ? assigneeRaw : null,
    ticketRef: ticketRaw.length > 0 ? ticketRaw : null,
    message: messageRaw.length > 0 ? messageRaw : null,
    contentMd: input.contentMd === undefined ? undefined : normalizeSpecContent(input.contentMd),
  };
};

/* ── Jeux de données d'aperçu (UPT-2079 L4, rest §4.3 à §4.8) ─────────────────────────────────────────── */

const ID_KEY = '_id';
const PREVIEW_ENTITY_CODE_PATTERN = /^[a-z][a-z0-9]{1,40}$/;
// eslint-disable-next-line no-control-regex -- caractères de contrôle refusés dans le nom d'un jeu (rest §4.3 point 2)
const PREVIEW_CONTROL_CHARS = /[\u0000-\u001f\u007f]/;
const PREVIEW_SOURCE_KINDS: readonly string[] = ['draft', 'version'];

const isInteger = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value);

/** Nom d'un jeu : `trim`, 1 à 120 caractères, sans caractère de contrôle (sinon 400 `invalid` `name`). */
const checkPreviewName = (name: unknown): string => {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  if (trimmed.length < 1 || trimmed.length > PREVIEW_LIMITS.nameMaxChars || PREVIEW_CONTROL_CHARS.test(trimmed)) {
    throw designError('preview.dataset.invalid', 'name');
  }
  return trimmed;
};

/** `kind` ∈ {draft, version} ; `version` entier ≥ 1 si et seulement si `kind = version` (sinon 400 `invalid` `source`). */
const checkPreviewSource = (source: unknown): PreviewSource => {
  const candidate = source as Partial<PreviewSource> | null | undefined;
  const kind = candidate?.kind;
  const version = candidate?.version ?? null;
  if (typeof kind !== 'string' || !PREVIEW_SOURCE_KINDS.includes(kind)) {
    throw designError('preview.dataset.invalid', 'source');
  }
  if (kind === 'version' ? !isInteger(version) || version < 1 : version !== null) {
    throw designError('preview.dataset.invalid', 'source');
  }
  return { kind: kind as PreviewSource['kind'], version };
};

const checkGenerateInput = (input: unknown, fallbackSeed: number | null): { count: number; seed: number } => {
  const candidate = input as Partial<PreviewGenerateInput> | null | undefined;
  const count = candidate?.count;
  if (!isInteger(count) || count < 0 || count > PREVIEW_LIMITS.generateMaxRows) {
    throw designError('preview.dataset.invalid', 'generate.count');
  }
  const seed = candidate?.seed ?? fallbackSeed ?? 1;
  if (!isInteger(seed) || seed < 0 || seed > PREVIEW_LIMITS.seedMax) {
    throw designError('preview.dataset.invalid', 'generate.seed');
  }
  return { count, seed };
};

/** Erreur du serveur correspondant à une erreur de validation des lignes (rest §4.4). */
const previewRowsError = (error: PreviewRowError): Error => {
  switch (error.kind) {
    case 'tooMany':
      return designError('preview.dataset.limit', 'rows');
    case 'tooLarge':
      return designError('preview.dataset.too_large', 'rows');
    default:
      return designError('preview.dataset.invalid', error.path ?? 'rows');
  }
};

/** Lignes normalisées ou erreur du serveur (la première erreur de la validation). */
const normalizePreviewRows = (rows: unknown, snapshot: PreviewSnapshot): PreviewRow[] => {
  const result = validateRows(rows, snapshot.entity.fields.map((field) => field.name));
  if ('errors' in result) {
    throw previewRowsError(result.errors[0]);
  }
  return result.rows;
};

/** Chaînes d'une valeur JSON, dans l'ordre du document (valeurs seulement, jamais les clés). */
const jsonStrings = (value: unknown, out: string[]): string[] => {
  if (typeof value === 'string') out.push(value);
  else if (Array.isArray(value)) value.forEach((entry) => jsonStrings(entry, out));
  else if (value && typeof value === 'object') Object.values(value as Record<string, unknown>).forEach((entry) => jsonStrings(entry, out));
  return out;
};

/**
 * Avertissements `pii.*` d'un jeu (rest §4.8) : nom → `name`, chaînes de l'instantané → `snapshot`, valeurs de lignes
 * (tableaux compris, par ligne puis par clé dans l'ordre de l'instantané) → `rows` ; agrégés par (code, source), `example` =
 * masque de la première occurrence, `firstRow` = rang à partir de 1 ; triés par code puis source. Jamais une valeur.
 */
const scanDatasetWarnings = (name: string, snapshot: PreviewSnapshot, rows: readonly PreviewRow[]): PreviewWarning[] => {
  const buckets = new Map<string, PreviewWarning>();
  const scan = (text: string, source: PreviewWarningSource, rank: number | null): void => {
    for (const hit of scanPersonalData(scanForm(text))) {
      const key = `${hit.code}|${source}`;
      const existing = buckets.get(key);
      if (existing) {
        existing.count += hit.count;
      } else {
        buckets.set(key, { code: hit.code as PreviewWarningCode, count: hit.count, example: hit.example, source, firstRow: rank });
      }
    }
  };
  scan(name, 'name', null);
  jsonStrings(snapshot, []).forEach((text) => scan(text, 'snapshot', null));
  rows.forEach((row, index) => {
    // Le serveur balaie `_id` d'abord, puis les champs dans l'ordre de l'instantané (rapport rest L4, écart 8).
    [ID_KEY, ...snapshot.entity.fields.map((field) => field.name)].forEach((key) => {
      jsonStrings(row[key], []).forEach((text) => scan(text, 'rows', index + 1));
    });
  });
  const order = (left: string, right: string): number => (left < right ? -1 : left > right ? 1 : 0);
  return [...buckets.values()].sort((a, b) => order(a.code, b.code) || order(a.source, b.source));
};

/** Empreinte des avertissements : SHA-256 du JSON canonique `[{code, count, source}]` trié ; `null` s'il n'y en a pas. */
const warningsFingerprintOf = async (warnings: readonly PreviewWarning[]): Promise<string | null> =>
  warnings.length === 0
    ? null
    : checksumOf(canonicalJson(warnings.map(({ code, count, source }) => ({ code, count, source }))));

/** Le `PreviewDataset` rendu : champs dérivés recalculés depuis l'acquittement conservé (jamais le champ `ack`). */
const presentPreviewDataset = (stored: PreviewStoredDataset): PreviewDataset => {
  const { ack, ...rest } = stored;
  const hasWarnings = stored.warningsFingerprint !== null;
  const valid = hasWarnings && ack !== null && ack.sha === stored.warningsFingerprint;
  return clone({
    ...rest,
    hasWarnings,
    warningsAcknowledged: valid,
    acknowledgedBy: valid && ack ? ack.by : null,
    acknowledgedAt: valid && ack ? ack.at : null,
    share: { active: false, suspended: false, expiresAt: null, createdBy: null, createdAt: null },
  });
};

const summarizePreviewDataset = (stored: PreviewStoredDataset): PreviewDatasetSummary => {
  const dataset = presentPreviewDataset(stored);
  return {
    id: dataset.id,
    dossierId: dataset.dossierId,
    name: dataset.name,
    entityCode: dataset.entityCode,
    entityLabel: dataset.entityLabel,
    moduleCode: dataset.moduleCode,
    source: dataset.source,
    specChecksum: dataset.specChecksum,
    capturedAt: dataset.capturedAt,
    rowCount: dataset.rowCount,
    seed: dataset.seed,
    revision: dataset.revision,
    hasWarnings: dataset.hasWarnings,
    warningsAcknowledged: dataset.warningsAcknowledged,
    share: dataset.share,
    createdBy: dataset.createdBy,
    createdAt: dataset.createdAt,
    updatedBy: dataset.updatedBy,
    updatedAt: dataset.updatedAt,
  };
};

/** `label.singular.fr` si c'est une chaîne non vide, sinon le code (une clé de traduction n'est pas un libellé : rapport rest L4, écart 14). */
const previewEntityLabel = (snapshot: PreviewSnapshot): string => {
  const singular = snapshot.entity.label.singular;
  const text = typeof singular === 'string' ? '' : singular.fr;
  return typeof text === 'string' && text.length > 0 ? text : snapshot.entity.code;
};

/** UPT-2079 L6a : le jeu figé du pipeline (jamais persisté : aucune écriture n'est possible en démonstration). */
type PipelineDemo = {
  environments: PipelineEnvironmentsPayload;
  tenants: PipelineTenantOption[];
  runs: PipelineRun[];
};
const seedPipeline = (): PipelineDemo => clone(pipelineFixture) as unknown as PipelineDemo;

/** Résumé d'une exécution : tous les champs sauf les étapes, la lecture, le plan, les portes et le compte rendu (comme `GET …/pipeline/runs`). */
const summarizePipelineRun = (run: PipelineRun): PipelineRunSummary => {
  const summary: Partial<PipelineRun> = { ...run };
  delete summary.steps;
  delete summary.code;
  delete summary.data;
  delete summary.gate;
  delete summary.logMd;
  return summary as PipelineRunSummary;
};

/** UPT-2079 L10a : le jeu figé des retours de recette (jamais persisté : aucune écriture n'est possible en démonstration). */
type FeedbackDemo = { items: Feedback[] };
const seedFeedback = (): FeedbackDemo => clone(feedbackFixture) as unknown as FeedbackDemo;

/** Ligne de liste d'un retour : les champs de `FeedbackSummary` seulement (comme `GET …/feedback`), jamais le journal ni le constat. */
const summarizeFeedback = (item: Feedback): FeedbackSummary => ({
  id: item.id,
  number: item.number,
  moduleCode: item.moduleCode,
  dossierId: item.dossierId,
  title: item.title,
  status: item.status,
  category: item.category,
  route: item.route,
  sourceKind: item.sourceKind,
  sourceRunId: item.sourceRunId,
  sourceStepId: item.sourceStepId,
  stepNumber: item.stepNumber,
  environmentCode: item.environmentCode,
  specPath: item.specPath,
  ticketRef: item.ticketRef,
  reportedBy: item.reportedBy,
  createdAt: item.createdAt,
  updatedAt: item.updatedAt,
});

/** Compteurs d'un périmètre (dossier ou module) : ils ignorent les filtres de statut, de catégorie et de destinataire (contrat `FeedbackCounts`). */
const countFeedback = (items: Feedback[]): FeedbackCounts => {
  const byStatus = Object.fromEntries(FEEDBACK_STATUSES.map((status) => [status, 0])) as FeedbackCounts['byStatus'];
  const byCategory = Object.fromEntries([...FEEDBACK_CATEGORIES, 'none'].map((category) => [category, 0])) as FeedbackCounts['byCategory'];
  let toTransmit = 0;
  items.forEach((item) => {
    byStatus[item.status] += 1;
    byCategory[item.category ?? 'none'] += 1;
    if (item.status === 'classified' && item.category === 'generator') {
      toTransmit += 1;
    }
  });
  return { byStatus, byCategory, toTransmit };
};

export class FixtureRepository implements ModuleStudioRepository {
  private faithfulState = createMemoryFaithfulRepository(() => [faithfulAuth(), faithfulTenant()]);
  getFaithfulState: ModuleStudioRepository['getFaithfulState'] = scope => this.faithfulState.get(scope);
  putFaithfulState: ModuleStudioRepository['putFaithfulState'] = (scope, input) => this.faithfulState.put(scope, input);
  private translationPreferences = new Map<string, boolean>();
  async getTranslationPreference(dossierId: string) { return { autoTranslateAfterDeposit: this.translationPreferences.get(dossierId) ?? false }; }
  async setTranslationPreference(dossierId: string, preference: TranslationPreference) {
    this.translationPreferences.set(dossierId, preference.autoTranslateAfterDeposit);
    return { ...preference };
  }
  createStarterTestPlan(): Promise<StarterTestPlanResult> { return Promise.reject(new Error('modulespec.not_served')); }
  estimateScreenAnalysis(): Promise<import('./screenAnalysisContracts').ScreenAnalysisEstimate> { return Promise.reject(new Error('screen.not_served')); }
  produceScreenCode():Promise<ScreenAnalysis>{return Promise.reject(new Error('screen.not_served'));}
  zoneCode():Promise<ZoneCodeResult>{return Promise.reject(new Error('screen.not_served'));}
  getScreenCodeSource():Promise<{path:string;source:string}>{return Promise.reject(new Error('screen.not_served'));}
  checkScreenCodeSource():Promise<import('./ModuleStudioRepository').ScreenCodeSourceCheck>{return Promise.reject(new Error('screen.not_served'));}
  saveScreenCodeSource():Promise<import('./ModuleStudioRepository').ScreenCodeSource>{return Promise.reject(new Error('screen.not_served'));}
  private screenAnalyses = new Map<string, { importId: string; analysis: ScreenAnalysis }>();
  async estimatePrototypeConversion(): Promise<import('./conversionContracts').PrototypeConversionEstimate> { throw new Error('prototype.conversion.capability'); }
  async startPrototypeConversion(): Promise<import('./conversionContracts').PrototypeConversionJob> { throw new Error('prototype.conversion.capability'); }
  async listPrototypeConversions(): Promise<import('./conversionContracts').PrototypeConversionJob[]> { return []; }
  async getPrototypeConversion(): Promise<import('./conversionContracts').PrototypeConversionJob> { throw new Error('prototype.conversion.capability'); }
  async cancelPrototypeConversion(): Promise<import('./conversionContracts').PrototypeConversionJob> { throw new Error('prototype.conversion.capability'); }
  async resumePrototypeConversion(): Promise<import('./conversionContracts').PrototypeConversionJob> { throw new Error('prototype.conversion.capability'); }
  async startScreenAnalysis(importId: string, input: ScreenAnalysisInput): Promise<ScreenAnalysis> {
    const source = await this.getPrototypeImport(importId);
    const base = this.requireModuleSpecVersion(source.dossierRef, input.versionRef);
    if (base.status !== 'accepted') throw new Error('screen.version_not_accepted');
    const entities = base.spec.entities as Record<string, { fields?: { name: string }[] }>;
    const candidates = [
      { entity: 'budgetline', name: 'monthly_equivalent' },
      { entity: 'goal', name: 'progress_ratio' },
    ];
    const computedFields = candidates.flatMap(candidate => {
      const field = (computedPilot.entities[candidate.entity as keyof typeof computedPilot.entities].fields as unknown as { name: string; type: 'amount' | 'number'; label: Record<string, string>; computed?: { formula: string; scale: number; emptyAs: 'absent' | 'zero' } }[]).find(item => item.name === candidate.name);
      return entities[candidate.entity] && field?.computed && !entities[candidate.entity].fields?.some(item => item.name === field.name) ? [{ entity: candidate.entity, name: field.name, type: field.type, label: field.label, computed: field.computed }] : [];
    });
    const analysis: ScreenAnalysis = { id: `screen-demo-${this.screenAnalyses.size + 1}`, createdAt: nowIso(), sourceChecksum: base.checksum, calls: 0, status: 'done', versionRef: input.versionRef, screenKey: input.screenKey, scope: 'dashboard', route: '', computedFields, dashboard: { layout: { columns: 12 }, widgets: [] } };
    this.screenAnalyses.set(analysis.id, { importId, analysis });
    return clone(analysis);
  }
  async listScreenAnalyses(importId: string): Promise<ScreenAnalysis[]> {
    return [...this.screenAnalyses.values()].filter(entry=>entry.importId===importId && ['done','running'].includes(entry.analysis.status)).map(entry=>clone(entry.analysis)).sort((a,b)=>Date.parse(b.createdAt)-Date.parse(a.createdAt));
  }
  async getScreenAnalysis(importId: string, id: string): Promise<ScreenAnalysis> {
    const entry = this.screenAnalyses.get(id);
    if (!entry || entry.importId !== importId) throw new Error('screen.not_found');
    return clone(entry.analysis);
  }
  async applyScreenAnalysis(importId: string, id: string): Promise<ScreenAnalysis> {
    const analysis = await this.getScreenAnalysis(importId, id);
    if (analysis.status !== 'done') throw new Error('screen.not_ready');
    const source = await this.getPrototypeImport(importId);
    const base = this.requireModuleSpecVersion(source.dossierRef, analysis.versionRef);
    const spec = clone(base.spec);
    const entities = spec.entities as Record<string, { fields: unknown[] }>;
    for (const proposal of analysis.computedFields ?? []) entities[proposal.entity].fields.push({ name: proposal.name, type: proposal.type, label: proposal.label, mode: 'READONLY', computed: proposal.computed });
    if (analysis.computedFields?.length) spec.specVersion = '1.4';
    const proposed = await this.proposeModuleSpecVersion(source.dossierRef, { spec, source: 'human' });
    const next = { ...analysis, status: 'applied' as const, resultVersion: proposed.version };
    this.screenAnalyses.set(id, { importId, analysis: next });
    return clone(next);
  }
  async rejectScreenAnalysis(importId: string, id: string): Promise<ScreenAnalysis> {
    const analysis = await this.getScreenAnalysis(importId, id);
    if (analysis.status !== 'done') throw new Error('screen.not_ready');
    const next = { ...analysis, status: 'rejected' as const };
    this.screenAnalyses.set(id, { importId, analysis: next });
    return clone(next);
  }
  private captures = new CaptureFixture();
  private captureUsers(dossier: string, hash: string): number[] {
    const refers = (value: unknown): boolean => {
      if (!value || typeof value !== 'object') return false;
      if ((value as { sha256?: unknown }).sha256 === hash) return true;
      return Object.values(value).some(refers);
    };
    return (this.state.moduleSpecVersions[dossier] ?? []).filter(version => ['proposed', 'accepted'].includes(version.status) && refers(version.spec)).map(version => version.version);
  }
  async listReferenceCaptures(dossier: string): Promise<ReferenceCapture[]> {
    this.requireDossier(dossier);
    return this.captures.list(dossier).map(row => ({ ...row, usedBy: this.captureUsers(dossier, row.sha256) }));
  }
  async uploadReferenceCapture(dossier: string, image: Blob, input: ReferenceCaptureInput): Promise<ReferenceCapture> {
    this.requireDossier(dossier);
    const source = await this.getPrototypeImport(input.importId);
    if (source.dossierRef !== dossier || !source.pieces.some(piece => piece.id === input.pieceId)) throw new Error('capture.not_found');
    return this.captures.upload(dossier, image, input, this.options.currentUserName?.() ?? 'Démo');
  }
  async getReferenceCaptureBytes(dossier: string, sha: string, thumb = true): Promise<ArrayBuffer> { this.requireDossier(dossier); return this.captures.bytes(dossier, sha, thumb); }
  async deleteReferenceCapture(dossier: string, sha: string): Promise<void> { this.requireDossier(dossier); this.captures.delete(dossier, sha, this.captureUsers(dossier, sha)); }

  getModuleComponents(): Promise<ModuleComponents> { return Promise.resolve({state:'ok',manifest:clone(moduleComponentExample) as ModuleComponents['manifest']}); }
  refreshModuleComponents(): Promise<ModuleComponents> { return this.getModuleComponents(); }
  proposeBindings(spec: ModuleSpecDocument, blocks: BindingBlock[]): Promise<BindingCandidate[][]> { return Promise.resolve(blocks.map(block => assistedBindings(spec as unknown as import('./contracts').DesignModuleSpecJson, block))); }
  getCapabilityCatalog(): Promise<CapabilityCatalog> { return Promise.resolve(clone(capabilityCatalog) as CapabilityCatalog); }
  async getPrototypeCoverage(id: string): Promise<CoverageReport> { return inventoryCoverage(await this.getPrototypeInventory(id), capabilityCatalog as CapabilityCatalog, (await this.getPrototypeImport(id)).pieces); }
  async getGapReport(id: string, version: number): Promise<GapReport> { const current = await this.getModuleSpecVersion(id, version); return gapReport({kind: current.prototypeImportId ? 'prototype' : 'module', dossier: id, version}, current.spec, fidelityLedger(current.spec, capabilityCatalog as CapabilityCatalog), [], (capabilityCatalog as CapabilityCatalog).codeGate!); }
  async getSpecCoverage(id: string, version: number): Promise<CoverageReport> { return modelCoverage((await this.getModuleSpecVersion(id, version)).spec, capabilityCatalog as CapabilityCatalog); }
  /** Démonstration : le menu « du prototype » est reconstitué du modèle (pages puis objets, un groupe, icônes suggérées par les mots du libellé). */
  async getNavigationProposal(id: string, version: number): Promise<NavigationProposal> {
    const spec = (await this.getModuleSpecVersion(id, version)).spec as Record<string, unknown>;
    const text = (value: unknown, fallback: string): string => (typeof value === 'string' ? value : typeof (value as { fr?: unknown } | null)?.fr === 'string' ? (value as { fr: string }).fr : fallback);
    const pages = Array.isArray(spec.pages) ? (spec.pages as Record<string, unknown>[]) : [];
    const entities = (spec.entities && typeof spec.entities === 'object' ? spec.entities : {}) as Record<string, { label?: { plural?: unknown } }>;
    const items: Record<string, unknown>[] = [
      ...pages.filter((page) => typeof page.key === 'string').map((page) => { const label = text(page.label, String(page.key)); const icon = suggestNavIcon(label); return { page: page.key, label: { fr: label }, ...(icon ? { icon } : {}) }; }),
      ...Object.keys(entities).map((code) => { const label = text(entities[code]?.label?.plural, code); const icon = suggestNavIcon(label); return { entity: code, label: { fr: label }, ...(icon ? { icon } : {}) }; }),
    ];
    if (items.length === 0) throw designError('navigation.no_menu');
    const module = (spec.module && typeof spec.module === 'object' ? spec.module : {}) as { name?: unknown };
    return { navigation: { menus: [{ key: 'menu', label: { fr: text(module.name, 'Menu') }, items }] }, notices: [] };
  }

  listSimulations(): Promise<SimulationSummary[]> { return Promise.resolve([]); }
  getSimulation(): Promise<Simulation | null> { return Promise.reject(new NotServedError('simulation', 'getSimulation')); }
  createSimulation(): Promise<Simulation> { return Promise.reject(new NotServedError('simulation', 'createSimulation')); }
  updateSimulation(): Promise<Simulation> { return Promise.reject(new NotServedError('simulation', 'updateSimulation')); }
  generateSimulationData(): Promise<Simulation> { return Promise.reject(new NotServedError('simulation', 'generateSimulationData')); }
  refreshSimulation(): Promise<SimulationRefreshResult> { return Promise.reject(new NotServedError('simulation', 'refreshSimulation')); }
  acknowledgeSimulationWarnings(): Promise<Simulation> { return Promise.reject(new NotServedError('simulation', 'acknowledgeSimulationWarnings')); }
  deleteSimulation(): Promise<void> { return Promise.reject(new NotServedError('simulation', 'deleteSimulation')); }
  createSimulationShare(): Promise<SimulationShareCreated> { return Promise.reject(new NotServedError('simulation', 'createSimulationShare')); }
  revokeSimulationShare(): Promise<void> { return Promise.reject(new NotServedError('simulation', 'revokeSimulationShare')); }

  private ideaFixture = new IdeaFixture(this);
  listIdeas: ModuleStudioRepository['listIdeas'] = (...args) => this.ideaFixture.listIdeas(...args);
  createIdea: ModuleStudioRepository['createIdea'] = (...args) => this.ideaFixture.createIdea(...args);
  getIdea: ModuleStudioRepository['getIdea'] = (...args) => this.ideaFixture.getIdea(...args);
  updateIdea: ModuleStudioRepository['updateIdea'] = (...args) => this.ideaFixture.updateIdea(...args);
  ideaEstimate: ModuleStudioRepository['ideaEstimate'] = (...args) => this.ideaFixture.ideaEstimate(...args);
  fetchIdeaRepository: ModuleStudioRepository['fetchIdeaRepository'] = (...args) => this.ideaFixture.fetchIdeaRepository(...args);
  getIdeaRepository: ModuleStudioRepository['getIdeaRepository'] = (...args) => this.ideaFixture.getIdeaRepository(...args);
  startBrief: ModuleStudioRepository['startBrief'] = (...args) => this.ideaFixture.startBrief(...args);
  acceptBrief: ModuleStudioRepository['acceptBrief'] = (...args) => this.ideaFixture.acceptBrief(...args);
  startPrototype: ModuleStudioRepository['startPrototype'] = (...args) => this.ideaFixture.startPrototype(...args);
  getPrototypeHtml: ModuleStudioRepository['getPrototypeHtml'] = (...args) => this.ideaFixture.getPrototypeHtml(...args);
  getIdeaPrompt: ModuleStudioRepository['getIdeaPrompt'] = (...args) => this.ideaFixture.getIdeaPrompt(...args);
  cancelIdea: ModuleStudioRepository['cancelIdea'] = (...args) => this.ideaFixture.cancelIdea(...args);
  transferIdea: ModuleStudioRepository['transferIdea'] = (...args) => this.ideaFixture.transferIdea(...args);
  deleteIdea: ModuleStudioRepository['deleteIdea'] = (...args) => this.ideaFixture.deleteIdea(...args);

  private state: FixtureState;

  private pipeline: PipelineDemo = seedPipeline();

  private feedback: FeedbackDemo = seedFeedback();

  constructor(private readonly options: FixtureRepositoryOptions = {}) {
    this.state = readStorage() ?? seedState();
  }

  /** « Réinitialiser » du bandeau : vide la clé et recharge les JSON d'origine (versions vidées). */
  reset(): void {
    this.captures.clear();
    clearStorage();
    this.state = seedState();
    this.pipeline = seedPipeline();
    this.feedback = seedFeedback();
    this.prototypeImports.clear();
    this.screenAnalyses.clear();
    this.assets.clear();
    this.ideaFixture = new IdeaFixture(this);
    this.demoTranslationJobs = new DemoTranslationJobs(this.demoTranslationHost);
  }

  /** Import des dossiers de démonstration (web §5.1) : oublie dossiers, specs et versions (de spec et de Module Spec), garde le reste. */
  dropDossiersSpecsAndVersions(): void {
    this.captures.clear();
    this.state.dossiers = [];
    this.state.specs = [];
    this.state.specVersions = {};
    this.state.moduleSpecVersions = {};
    this.state.moduleSpecDrafts = {};
    this.state.previewDatasets = {};
    this.state.generationRuns = seedGenerationRuns();
  }

  private persist(): void {
    writeStorage(this.state);
  }

  // ── Compteurs calculés ──────────────────────────────────────────────────

  private withCounters(dossier: DesignDossier): DesignDossier {
    const copy = clone(dossier);
    copy.specCount = this.state.specs.filter((spec) => spec.dossierId === dossier.id).length;
    const planIds = this.state.plans
      .filter((plan) => plan.dossierId === dossier.id)
      .map((plan) => plan.id);
    copy.testPlanCount = planIds.length;
    copy.openTestRuns = this.state.runs.filter(
      (run) => planIds.includes(run.planId) && run.status === 'open'
    ).length;
    return copy;
  }

  private summarizeRun(run: TestRun): TestRunSummary {
    const plan = this.state.plans.find((candidate) => candidate.id === run.planId);
    const total = plan
      ? plan.scenes.reduce((count, scene) => count + scene.steps.length, 0)
      : 0;
    const results = Object.values(run.results ?? {});
    return {
      id: run.id,
      planId: run.planId,
      environmentCode: run.environmentCode,
      testerKind: run.testerKind,
      testerName: run.testerName,
      status: run.status,
      done: results.filter((result) => isDoneVerdict(result.verdict)).length,
      failed: results.filter(
        (result) => result.verdict === 'fail' || result.verdict === 'blocked'
      ).length,
      proposed: results.filter((result) => result.verdict === 'proposed').length,
      total,
      startedAt: run.startedAt,
      closedAt: run.closedAt,
    };
  }

  private summarizePlan(plan: TestPlan): TestPlanSummary {
    const runs = this.state.runs
      .filter((run) => run.planId === plan.id)
      .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
    const lastRun = runs[0] ?? null;
    return {
      id: plan.id,
      dossierId: plan.dossierId,
      moduleCode: plan.moduleCode,
      code: plan.code,
      title: plan.title,
      status: plan.status,
      sceneCount: plan.scenes.length,
      stepCount: plan.scenes.reduce((count, scene) => count + scene.steps.length, 0),
      environmentCodes: [...plan.environmentCodes],
      lastRun: lastRun
        ? {
            id: lastRun.id,
            environmentCode: lastRun.environmentCode,
            done: this.summarizeRun(lastRun).done,
            total: this.summarizeRun(lastRun).total,
            status: lastRun.status,
          }
        : null,
      // Le plan de démonstration est statique : dernière exécution connue, à
      // défaut la date du jour (revue CORE d'intégration du 11.10 : la référence
      // fixe du jeu décalait les dates relatives d'un jour).
      updatedAt: lastRun?.startedAt ?? nowIso(),
    };
  }

  // ── Dossiers ────────────────────────────────────────────────────────────

  async listDossiers(): Promise<DesignDossier[]> {
    return this.state.dossiers.map((dossier) => this.withCounters(dossier));
  }

  async getDossierThread(id: string): Promise<DossierThread> {
    const rows = await this.listModuleSpecVersions(id);
    const current = rows.find(row => row.status === 'proposed' || row.status === 'accepted') ?? null;
    const inService = rows.find(row => row.status === 'accepted') ?? null;
    const draft = await this.getModuleSpecDraft(id);
    const stale = current && draft && current.version !== draft.basedOnVersion;
    return { current, inService, draft, analysis: { latest: null, displayed: null }, events: [],
      primaryAction: { code: current?.status === 'proposed' && current.errorCount ? 'correct' : current?.status === 'proposed' ? 'accept' : stale ? 'restart' : 'simulate', target: 'version/' + (current?.version ?? 0) },
      forbidden: stale ? [{ action: 'propose', reason: 'modulespec.draft.stale_base' }, { action: 'resume', reason: 'modulespec.draft.stale_base' }] : [] };
  }
  async getDossier(id: string): Promise<DesignDossier | null> {
    const dossier = this.state.dossiers.find((candidate) => candidate.id === id);
    return dossier ? this.withCounters(dossier) : null;
  }

  /**
   * Création (sans `id`) : règles du serveur (rest §4.3) — code normalisé (trim, minuscules, motif
   * `dossierCodePattern`), 409 dossier.code.taken si un dossier a ce code OU cet identifiant ; mise à jour
   * (avec `id`) : le code du jeu de démonstration reste libre (identifiant « dz-… » distinct du code).
   */
  async saveDossier(input: DesignDossierInput): Promise<DesignDossier> {
    const existing = input.id
      ? this.state.dossiers.find((candidate) => candidate.id === input.id)
      : undefined;
    if (existing) {
      existing.name = normalizeDossierName(input.name);
      existing.summary = normalizeSummary(input.summary);
      existing.status = normalizeDossierStatus(input.status);
      existing.moduleCode = normalizeModuleCode(input.moduleCode);
      existing.ticketRefs = normalizeTicketRefs(input.ticketRefs);
      const opportunityRef = normalizeRef(input.opportunityRef, 'opportunityRef');
      if (opportunityRef !== undefined) {
        existing.opportunityRef = opportunityRef;
      }
      const projectRef = normalizeRef(input.projectRef, 'projectRef');
      if (projectRef !== undefined) {
        existing.projectRef = projectRef;
      }
      const changeRequestRef = normalizeRef(input.changeRequestRef, 'changeRequestRef');
      if (changeRequestRef !== undefined) {
        existing.changeRequestRef = changeRequestRef;
      }
      existing.updatedAt = nowIso();
      this.persist();
      return this.withCounters(existing);
    }
    const code = (input.code ?? '').trim().toLowerCase();
    if (!DOSSIER_CODE_PATTERN.test(code)) {
      throw designError('dossier.invalid', 'code');
    }
    if (this.state.dossiers.some((candidate) => candidate.code === code || candidate.id === code)) {
      throw designError('dossier.code.taken');
    }
    const created: DesignDossier = {
      id: `dz-${randomSuffix()}`,
      code,
      name: normalizeDossierName(input.name),
      summary: normalizeSummary(input.summary),
      status: normalizeDossierStatus(input.status),
      moduleCode: normalizeModuleCode(input.moduleCode),
      opportunityRef: normalizeRef(input.opportunityRef, 'opportunityRef') ?? null,
      projectRef: normalizeRef(input.projectRef, 'projectRef') ?? null,
      ticketRefs: normalizeTicketRefs(input.ticketRefs),
      ownerName: this.options.currentUserName?.() ?? '',
      moduleSpecVersion: 0,
      specCount: 0,
      testPlanCount: 0,
      openTestRuns: 0,
      lastPromotion: null,
      changeRequestRef: normalizeRef(input.changeRequestRef, 'changeRequestRef') ?? null,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    };
    this.state.dossiers.push(created);
    this.persist();
    return this.withCounters(created);
  }

  private requireDossier(id: string): DesignDossier {
    const dossier = this.state.dossiers.find((candidate) => candidate.id === id);
    if (!dossier) {
      throw designError('dossier.not_found');
    }
    return dossier;
  }

  /** 409 dossier.in_use (`test-plans` puis `prototype-imports`) ; sinon supprime dossier, specs et versions. */
  async deleteDossier(id: string): Promise<void> {
    this.requireDossier(id);
    if (this.state.plans.some((plan) => plan.dossierId === id)) {
      throw designError('dossier.in_use', 'test-plans');
    }
    if ([...this.prototypeImports.values()].some((entry) => entry.record.dossierRef === id)) {
      throw designError('dossier.in_use', 'prototype-imports');
    }
    for (const spec of this.state.specs.filter((candidate) => candidate.dossierId === id)) {
      delete this.state.specVersions[spec.id];
    }
    this.state.specs = this.state.specs.filter((candidate) => candidate.dossierId !== id);
    this.state.dossiers = this.state.dossiers.filter((candidate) => candidate.id !== id);
    delete this.state.moduleSpecVersions[id];
    delete this.state.moduleSpecDrafts[id];
    delete this.state.previewDatasets[id];
    delete this.state.generationRuns[id];
    this.assets.deleteDossier(id);
    this.captures.clear(id);
    this.persist();
  }

  /** Projection du dossier (UPT-2095 §11.1) : dernière version acceptée, sinon proposée, sinon `spec` null. */
  async getModuleSpec(dossierId: string): Promise<DesignModuleSpecPayload> {
    this.requireDossier(dossierId);
    const projection = projectionOf(this.moduleSpecVersionsOf(dossierId));
    return { version: projection.version, spec: projection.spec, status: projection.status };
  }

  /** L2, comportement UPT-2095 §11.1 : crée une version `proposed` `human` ; la réponse porte la projection. */
  async putModuleSpec(dossierId: string, input: DesignModuleSpecInput): Promise<DesignDossier> {
    const dossier = this.requireDossier(dossierId);
    await this.proposeModuleSpecVersion(dossierId, { spec: input?.spec as unknown as ModuleSpecDocument, source: 'human' });
    return this.withCounters(dossier);
  }

  /* ── Versions de Module Spec (UPT-2095 §11.2) ─────────────────────────── */

  private moduleSpecVersionsOf(dossierId: string): ModuleSpecVersion[] {
    return this.state.moduleSpecVersions[dossierId] ?? [];
  }

  private requireModuleSpecVersion(dossierId: string, version: number): ModuleSpecVersion {
    const found = this.moduleSpecVersionsOf(dossierId).find((entry) => entry.version === version);
    if (!found) {
      throw designError('modulespec.not_found');
    }
    return found;
  }

  /** Recalcule `moduleSpecVersion` / `moduleSpecStatus` du dossier (seulement si l'un des deux change). */
  private refreshModuleSpecProjection(dossierId: string): void {
    const dossier = this.state.dossiers.find((candidate) => candidate.id === dossierId);
    if (!dossier) return;
    const projection = projectionOf(this.moduleSpecVersionsOf(dossierId));
    if (dossier.moduleSpecVersion !== projection.version || (dossier.moduleSpecStatus ?? null) !== projection.status) {
      dossier.moduleSpecVersion = projection.version;
      dossier.moduleSpecStatus = projection.status;
      dossier.updatedAt = nowIso();
    }
  }

  /** Résumés, version décroissante (ordre du serveur) ; dossier inconnu du jeu → liste vide. */
  async listModuleSpecVersions(dossierId: string): Promise<ModuleSpecVersionSummary[]> {
    return [...this.moduleSpecVersionsOf(dossierId)]
      .sort((left, right) => right.version - left.version)
      .map(toModuleSpecSummary);
  }

  async getModuleSpecVersion(dossierId: string, version: number): Promise<ModuleSpecVersion> {
    return clone(this.requireModuleSpecVersion(dossierId, version));
  }

  async diffModuleSpecVersions(dossierId: string, from: number, to: number): Promise<ModuleSpecDiffSection[]> {
    const left = this.requireModuleSpecVersion(dossierId, from);
    const right = this.requireModuleSpecVersion(dossierId, to);
    return moduleSpecLocalDiff(left.spec, right.spec);
  }

  /**
   * Règles du serveur (rest §11.1) : contrôles L2 (400 / 413), `source` ∉ {human, import} → 400 source_forbidden,
   * rapport jamais bloquant, dédoublonnage par checksum avec la dernière version non rejetée, version suivante
   * `proposed`, projection recalculée. Estampille `rights` et `provenance.sourceDossier` non reproduite (serveur).
   */
  async proposeModuleSpecVersion(dossierId: string, input: ModuleSpecProposeInput): Promise<ModuleSpecVersion> {
    this.requireDossier(dossierId);
    const spec = input?.spec as Record<string, unknown> | null | undefined;
    if (!spec || typeof spec !== 'object' || Array.isArray(spec)) {
      throw designError('dossier.module_spec.invalid', 'spec');
    }
    // UPT-2079 L14a : le serveur accepte « 1.2 » (habillage, pages sur mesure) en plus de « 1.0 » et « 1.1 ».
    if (!supportedSpecVersion(spec.specVersion)) {
      throw designError('dossier.module_spec.invalid', 'specVersion');
    }
    for (const field of ['module', 'entities'] as const) {
      const value = spec[field];
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw designError('dossier.module_spec.invalid', field);
      }
    }
    if (utf8Bytes(JSON.stringify(spec)) > DESIGN_LIMITS.moduleSpecMaxBytes) {
      throw designError('dossier.module_spec.too_large');
    }
    const source = input.source ?? 'human';
    if (source !== 'human' && source !== 'import') {
      throw designError('modulespec.source_forbidden', String(source));
    }
    if (input.comment !== undefined && (typeof input.comment !== 'string' || input.comment.length > MODULE_SPEC_COMMENT_MAX)) {
      throw designError('modulespec.invalid', 'comment');
    }
    const document = clone(spec) as ModuleSpecDocument;
    const checksum = await checksumOf(canonicalJson(document));
    const versions = this.moduleSpecVersionsOf(dossierId);
    const last = versions.length > 0 ? versions[versions.length - 1] : null;
    if (last && last.status !== 'rejected' && last.checksum === checksum) {
      return clone(last);
    }
    const validation = moduleSpecLocalCheck(document);
    const created: ModuleSpecVersion = {
      dossierRef: dossierId,
      version: (last?.version ?? 0) + 1,
      specVersion: document.specVersion,
      moduleCode: typeof document.module.code === 'string' ? document.module.code : null,
      source,
      status: 'proposed',
      errorCount: validation.errors.length,
      warningCount: validation.warnings.length,
      coverage: validation.scores.coverage,
      authoredBy: this.options.currentUserName?.() || 'Démo',
      createdAt: nowIso(),
      decidedBy: null,
      decidedAt: null,
      prototypeImportId: null,
      spec: document,
      checksum,
      validation,
      decisionComment: null,
    };
    this.state.moduleSpecVersions[dossierId] = [...versions, created];
    this.refreshModuleSpecProjection(dossierId);
    this.persist();
    return clone(created);
  }

  /** `not_proposed` (409, message = statut), `has_errors` (400), `comment_required` (400) ; acceptée précédente → `superseded`, propositions plus anciennes → écartées (`rejected`). */
  async decideModuleSpecVersion(
    dossierId: string,
    version: number,
    input: ModuleSpecDecisionInput
  ): Promise<ModuleSpecVersion> {
    const target = this.requireModuleSpecVersion(dossierId, version);
    if (target.status !== 'proposed') {
      throw designError('modulespec.not_proposed', target.status);
    }
    const comment = typeof input?.comment === 'string' ? input.comment.trim() : '';
    if (comment.length > MODULE_SPEC_COMMENT_MAX) {
      throw designError('modulespec.comment_required', 'too_long');
    }
    const actor = this.options.currentUserName?.() || 'Démo';
    if (input?.decision === 'accept') {
      if (target.errorCount > 0) {
        throw designError('modulespec.has_errors', String(target.errorCount));
      }
      // UPT-2079 L12b : comme le serveur, après le contrôle des erreurs — l'étape Livrer échouerait → 409, message = le code de la première erreur du contrôle.
      const delivery = this.deliveryCheckOf(target);
      if (delivery.errors.length > 0) {
        throw designError('modulespec.delivery_blocked', delivery.errors[0].code);
      }
      // Une version n'est jamais supprimée : la version en service précédente devient « ancienne », et — comme le serveur — les propositions plus anciennes encore à relire
      // sont écartées automatiquement (statut `rejected`, commentaire fixé), elles restent consultables dans l'historique.
      const discardedAt = nowIso();
      this.moduleSpecVersionsOf(dossierId).forEach((entry) => {
        if (entry.status === 'accepted') entry.status = 'superseded';
        if (entry.status === 'proposed' && entry.version < target.version) {
          entry.status = 'rejected';
          entry.decidedBy = actor;
          entry.decidedAt = discardedAt;
          entry.decisionComment = `Écartée automatiquement : la version ${target.version} a été acceptée.`;
        }
      });
      target.status = 'accepted';
    } else if (input?.decision === 'reject') {
      if (comment.length === 0) {
        throw designError('modulespec.comment_required');
      }
      target.status = 'rejected';
    } else {
      throw designError('modulespec.invalid', 'decision');
    }
    target.decidedBy = actor;
    target.decidedAt = nowIso();
    target.decisionComment = comment.length > 0 ? comment : null;
    this.refreshModuleSpecProjection(dossierId);
    this.persist();
    return clone(target);
  }

  /**
   * La réparation locale d'une version `proposed` : le document réparé, son rapport (une remarque `autofix` par correction) et l'aperçu
   * (`repairable = errorsBefore − errorsAfter`, 0 sans correction ; comme le serveur, il ne compte que les erreurs : les corrections du socle et des noms sont dans
   * `changes`). Une version qui n'est pas `proposed` n'a rien à réparer : aperçu à 0.
   */
  private repairPlan(target: ModuleSpecVersion) {
    const errorsBefore = target.validation.errors.length;
    // L12a, aligné le 18.10 (W23) : comme le serveur, le socle d'abord et les noms déjà pris ne s'appliquent qu'aux sources MACHINE (agent, MCP,
    // prototype) ; une proposition humaine ou importée n'est pas retouchée.
    const coreFirst = target.source === 'agent' || target.source === 'mcp' || target.source === 'prototype';
    const { spec, changes } = target.status === 'proposed' ? moduleSpecLocalRepair(target.spec, { coreFirst }) : { spec: target.spec, changes: [] };
    const repaired = moduleSpecLocalCheck(spec);
    const errorsAfter = changes.length === 0 ? errorsBefore : repaired.errors.length;
    const infos = changes.map((change) => ({ code: 'autofix', path: change.path, message: AUTOFIX_MESSAGES[change.kind] ?? AUTOFIX_MESSAGES['workflow.state_key'] }));
    const preview: ModuleSpecRepairPreview = {
      version: target.version,
      errorsBefore,
      errorsAfter,
      repairable: changes.length === 0 ? 0 : Math.max(0, errorsBefore - errorsAfter),
      changes,
    };
    return { spec, validation: infos.length > 0 ? { ...repaired, infos } : repaired, preview };
  }

  /** Aperçu de la réparation (UPT-2079 L11 §9), rien n'est écrit ; ne rend jamais 409 (version décidée ou rien à corriger : `repairable` 0) ; 404 version inconnue. */
  async previewRelations(_input:RelationsInput):Promise<RelationsResult>{throw new Error('relations.server.required');}
  async applyRelations(_input:RelationsInput):Promise<RelationsResult>{throw new Error('relations.server.required');}
  async previewModuleSpecRepair(dossierId: string, version: number): Promise<ModuleSpecRepairPreview> {
    const target = this.requireModuleSpecVersion(dossierId, version);
    return clone(this.repairPlan(target).preview);
  }

  /**
   * Nouvelle version `proposed` (source et import de prototype repris de la version réparée, `authoredBy` suffixé), la version `version` reste inchangée ;
   * 409 `modulespec.not_proposed`, 409 `modulespec.repair.nothing` ; un second appel rend la version déjà créée (dédoublonnage par empreinte).
   */
  async repairModuleSpecVersion(dossierId: string, version: number): Promise<ModuleSpecVersion> {
    const target = this.requireModuleSpecVersion(dossierId, version);
    if (target.status !== 'proposed') {
      throw designError('modulespec.not_proposed', target.status);
    }
    const plan = this.repairPlan(target);
    // F4a : la migration des pages obsolètes peut être proposée sans diminution du nombre d'erreurs.
    if (plan.preview.repairable === 0 && !plan.preview.changes.some(change => change.kind === 'page.kind_other_migrated' || change.kind === 'reference.identifier')) {
      throw designError('modulespec.repair.nothing');
    }
    const document = clone(plan.spec) as ModuleSpecDocument;
    const versions = this.moduleSpecVersionsOf(dossierId);
    const last = versions[versions.length - 1];
    const checksum = await checksumOf(canonicalJson(document));
    if (last.status !== 'rejected' && last.checksum === checksum) {
      return clone(last);
    }
    const created: ModuleSpecVersion = {
      dossierRef: dossierId,
      version: last.version + 1,
      specVersion: document.specVersion,
      moduleCode: typeof document.module.code === 'string' ? document.module.code : null,
      source: target.source,
      status: 'proposed',
      errorCount: plan.validation.errors.length,
      warningCount: plan.validation.warnings.length,
      coverage: plan.validation.scores.coverage,
      authoredBy: `${this.options.currentUserName?.() || 'Démo'} (réparation automatique)`,
      createdAt: nowIso(),
      decidedBy: null,
      decidedAt: null,
      prototypeImportId: target.prototypeImportId ?? null,
      spec: document,
      checksum,
      validation: plan.validation,
      decisionComment: null,
    };
    this.state.moduleSpecVersions[dossierId] = [...versions, created];
    this.refreshModuleSpecProjection(dossierId);
    this.persist();
    return clone(created);
  }

  /** L'exemple de contrôle de livraison d'une version du dépôt local (`moduleSpecLocalDeliveryCheck` : un exemple, jamais le générateur). */
  private deliveryCheckOf(target: ModuleSpecVersion): DeliveryCheck {
    return moduleSpecLocalDeliveryCheck({
      version: target.version,
      spec: target.spec,
      warnings: target.validation.warnings.length,
      notes: (target.validation.infos ?? []).length,
      checkedAt: nowIso(),
    });
  }

  /**
   * Contrôle de livraison à blanc (UPT-2079 L12b), rien n'est écrit, pas de cache : un EXEMPLE figé — `ok: false` avec `entity.taken` sur `/entities/<code>` pour un objet dont le
   * code est déjà pris par la plateforme (`auditevent` de la version 1 de `dz-cmdb`), `ok: true` pour tout autre document ; 404 version inconnue.
   */
  async estimateModuleSpecTranslation(dossierId: string, version: number): Promise<ModuleSpecTranslationEstimate> {
    this.requireModuleSpecVersion(dossierId, version);
    return { version, locales: ['de','it','ar'], texts: 189, toTranslate: 189, skippedPii: 0, skippedOther: 0,
      batches: 3, estimatedPromptTokens: 8200, outputMaxTokens: 6000, route: 'geode', provider: 'OPENROUTER', model: 'claude-haiku-5-5',
      estimatedCostChf: 0.0079, quota: { periodStart: '2026-10-01', analysesUsed: 0, analysesMax: 10, tokensUsed: 0, tokensMax: 300000, exhausted: false }, reason: null,
      // P2b : le découpage de la tâche de démonstration (15 lots, un par seconde dans le temps de la démonstration, 20 s annoncées par lot dans la vraie).
      perLocale: [{ locale: 'de', texts: 189, batches: 5 }, { locale: 'it', texts: 189, batches: 5 }, { locale: 'ar', texts: 189, batches: 5 }], estimatedSeconds: 300, jobs: true };
  }
  async translateModuleSpecVersion(dossierId: string, version: number): Promise<ModuleSpecVersion> {
    if (!isModuleStudioDemoProfile()) throw new NotServedError('translation','translateModuleSpecVersion');
    return this.createTranslatedDemoVersion(dossierId, version);
  }
  /** P2b : une tâche de fond de démonstration qui avance d'un lot par seconde, puis crée la version traduite (hors démonstration : `NotServedError` pour les écritures). */
  private readonly demoTranslationHost: DemoTranslationHost = {
    assertTranslatable: (dossierId, version) => {
      const target = this.requireModuleSpecVersion(dossierId, version);
      if (target.status !== 'proposed' && target.status !== 'accepted') throw designError('modulespec.translate.state', target.status);
      if (translateDemoLabels(clone(target.spec)) === 0) throw designError('modulespec.translate.nothing');
    },
    materialize: (dossierId, version) => this.createTranslatedDemoVersion(dossierId, version),
    author: () => this.options.currentUserName?.() || 'Démo',
  };
  private demoTranslationJobs = new DemoTranslationJobs(this.demoTranslationHost);
  async createModuleSpecTranslationJob(dossierId: string, version: number): Promise<ModuleSpecTranslationJob> {
    if (!isModuleStudioDemoProfile()) throw new NotServedError('translationJobs', 'createModuleSpecTranslationJob');
    return this.demoTranslationJobs.create(dossierId, version);
  }
  async listModuleSpecTranslationJobs(dossierId: string, limit?: number): Promise<ModuleSpecTranslationJob[]> {
    return this.demoTranslationJobs.list(dossierId, limit);
  }
  async getModuleSpecTranslationJob(dossierId: string, jobId: string): Promise<ModuleSpecTranslationJob> {
    return this.demoTranslationJobs.get(dossierId, jobId);
  }
  async cancelModuleSpecTranslationJob(dossierId: string, jobId: string): Promise<ModuleSpecTranslationJob> {
    if (!isModuleStudioDemoProfile()) throw new NotServedError('translationJobs', 'cancelModuleSpecTranslationJob');
    return this.demoTranslationJobs.cancel(dossierId, jobId);
  }
  async resumeModuleSpecTranslationJob(dossierId: string, jobId: string): Promise<ModuleSpecTranslationJob> {
    if (!isModuleStudioDemoProfile()) throw new NotServedError('translationJobs', 'resumeModuleSpecTranslationJob');
    return this.demoTranslationJobs.resume(dossierId, jobId);
  }
  private async createTranslatedDemoVersion(dossierId: string, version: number): Promise<ModuleSpecVersion> {
    const target = this.requireModuleSpecVersion(dossierId, version);
    if (target.status !== 'proposed' && target.status !== 'accepted') throw designError('modulespec.translate.state',target.status);
    const document = clone(target.spec);
    if (translateDemoLabels(document) === 0) throw designError('modulespec.translate.nothing');
    const versions = this.moduleSpecVersionsOf(dossierId);
    const validation = moduleSpecLocalCheck(document);
    const created: ModuleSpecVersion = { ...target, version: Math.max(...versions.map(entry => entry.version)) + 1,
      status: 'proposed', spec: document, checksum: await checksumOf(canonicalJson(document)),
      validation, errorCount: validation.errors.length, warningCount: validation.warnings.length, coverage: validation.scores.coverage,
      authoredBy: `${this.options.currentUserName?.() || 'Démo'} (traduction automatique)`, createdAt: nowIso(),
      decidedBy: null, decidedAt: null, decisionComment: null };
    this.state.moduleSpecVersions[dossierId] = [...versions, created];
    this.refreshModuleSpecProjection(dossierId);
    return clone(created);
  }

  async getDeliveryCheck(dossierId: string, version: number): Promise<DeliveryCheck> {
    return clone(this.deliveryCheckOf(this.requireModuleSpecVersion(dossierId, version)));
  }

  /** Démonstration : approximation `moduleSpecLocalCheck` (pas le schéma complet). */
  async validateModuleSpec(spec: ModuleSpecDocument): Promise<ValidationReport> {
    return moduleSpecLocalCheck(spec);
  }

  /** JSON lu par `JSON.parse` puis contrôle local ; YAML non lu en démonstration (400 modulespec.invalid, message `yaml`). */
  async validateModuleSpecText(text: string, format: 'json' | 'yaml'): Promise<ValidationReport> {
    if (format === 'yaml') {
      throw designError('modulespec.invalid', 'yaml');
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw designError('modulespec.invalid', 'json');
    }
    return moduleSpecLocalCheck(parsed);
  }

  /* ── Concepteur (UPT-2079 L3, web §4.3, rest §4.3 à §4.5) ─────────────── */

  private draftAuthor(): string {
    return this.options.currentUserName?.() || 'Démo';
  }

  /** Contrôles de forme L2 (`specVersion`, `module`, `entities`, 2 MiB) : 400 dossier.module_spec.invalid / 413 too_large. */
  private checkDraftShape(spec: unknown): Record<string, unknown> {
    const record = spec as Record<string, unknown> | null | undefined;
    if (!record || typeof record !== 'object' || Array.isArray(record)) {
      throw designError('dossier.module_spec.invalid', 'spec');
    }
    // UPT-2079 L14a : « 1.2 » (habillage) accepté comme par le serveur.
    if (!supportedSpecVersion(record.specVersion)) {
      throw designError('dossier.module_spec.invalid', 'specVersion');
    }
    for (const field of ['module', 'entities'] as const) {
      const value = record[field];
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw designError('dossier.module_spec.invalid', field);
      }
    }
    if (utf8Bytes(JSON.stringify(record)) > DESIGNER_LIMITS.draftMaxBytes) {
      throw designError('dossier.module_spec.too_large');
    }
    return record;
  }

  /** Bornes `DESIGNER_LIMITS` dans l'ordre du rest §4.3 point 2 : 400 modulespec.draft.limit, message = clé de la borne. */
  private checkDraftLimits(spec: Record<string, unknown>): void {
    const asArr = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
    const entities = Object.values((spec.entities ?? {}) as Record<string, unknown>).filter(
      (entity): entity is Record<string, unknown> => !!entity && typeof entity === 'object' && !Array.isArray(entity)
    );
    if (entities.length > DESIGNER_LIMITS.entities) throw designError('modulespec.draft.limit', 'entities');
    if (entities.some((entity) => asArr(entity.fields).length > DESIGNER_LIMITS.fieldsPerEntity)) throw designError('modulespec.draft.limit', 'fieldsPerEntity');
    if (asArr(spec.relations).length > DESIGNER_LIMITS.relations) throw designError('modulespec.draft.limit', 'relations');
    if (asArr(spec.pages).length > DESIGNER_LIMITS.pages) throw designError('modulespec.draft.limit', 'pages');
    if (entities.some((entity) => asArr(entity.rules).length > DESIGNER_LIMITS.rulesPerEntity)) throw designError('modulespec.draft.limit', 'rulesPerEntity');
    if (entities.some((entity) => asArr((entity.workflow as Record<string, unknown> | undefined)?.states).length > DESIGNER_LIMITS.workflowStates)) {
      throw designError('modulespec.draft.limit', 'workflowStates');
    }
    const tooManyOptions = entities.some((entity) =>
      asArr(entity.fields).some((field) => {
        const data = (field as Record<string, unknown> | null)?.data as Record<string, unknown> | undefined;
        return asArr(data?.options).length > DESIGNER_LIMITS.staticOptions;
      })
    );
    if (tooManyOptions) throw designError('modulespec.draft.limit', 'staticOptions');
  }

  /** `layout` nettoyé (clés hors document retirées, coordonnées arrondies) et borné : 400 modulespec.draft.layout_invalid. */
  private cleanLayout(spec: Record<string, unknown>, layout: unknown): ModuleSpecDraftLayout {
    const record = layout as Record<string, unknown> | null;
    if (!record || typeof record !== 'object' || Array.isArray(record)) throw designError('modulespec.draft.layout_invalid', 'layout');
    const entities = record.entities as Record<string, unknown> | null | undefined;
    if (!entities || typeof entities !== 'object' || Array.isArray(entities)) throw designError('modulespec.draft.layout_invalid', 'layout');
    const codes = Object.keys((spec.entities ?? {}) as Record<string, unknown>);
    const out: Record<string, DesignerNodePosition> = {};
    Object.keys(entities).forEach((code) => {
      if (!codes.includes(code)) return;
      const position = entities[code] as Record<string, unknown> | null;
      const x = Math.round(Number(position?.x));
      const y = Math.round(Number(position?.y));
      if (!position || typeof position !== 'object' || !Number.isFinite(x) || !Number.isFinite(y)) {
        throw designError('modulespec.draft.layout_invalid', 'layout');
      }
      if (Math.abs(x) > DESIGNER_LIMITS.layoutCoordinateMax || Math.abs(y) > DESIGNER_LIMITS.layoutCoordinateMax) {
        throw designError('modulespec.draft.layout_invalid', code);
      }
      out[code] = { x, y };
    });
    const result: ModuleSpecDraftLayout = { entities: out };
    if (record.screenReviews != null) {
      const reviews = record.screenReviews as NonNullable<ModuleSpecDraftLayout['screenReviews']>;
      if (typeof reviews !== 'object' || Array.isArray(reviews) || Object.keys(reviews).length > 1200 || JSON.stringify(reviews).length > 2097152) throw designError('modulespec.draft.layout_invalid', 'screenReviews');
      const keys = new Set<string>();
      const collect = (blocks: unknown, prefix: string, depth: number) => {
        if (depth > 3 || !Array.isArray(blocks)) return;
        for (const block of blocks) { const key = `${prefix}/${block.key}`; keys.add(key); collect(block.children,key,depth+1); }
      };
      for (const page of (Array.isArray(spec.pages) ? spec.pages : [])) collect(page.screen?.blocks,page.key,0);
      result.screenReviews = {};
      for (const [key,value] of Object.entries(reviews)) {
        if (!keys.has(key)) continue;
        if (!value || !['pending','accepted','rejected','edited'].includes(value.status) || typeof value.before !== 'string' || value.before.length > 524288) throw designError('modulespec.draft.layout_invalid', 'screenReviews');
        result.screenReviews[key] = {status:value.status,before:value.before};
        if(value.content!==undefined){if(typeof value.content!=='string'||value.content.length>524288)throw designError('modulespec.draft.layout_invalid','screenReviews');result.screenReviews[key].content=value.content;}
      }
    }
    return result;
  }

  private async buildDraft(
    dossierId: string,
    spec: Record<string, unknown>,
    origin: ModuleSpecDraft['origin'],
    basedOnVersion: number | null,
    layout: ModuleSpecDraftLayout,
    createdAt: string
  ): Promise<ModuleSpecDraft> {
    const document = clone(spec) as ModuleSpecDocument;
    const validation = moduleSpecLocalCheck(document);
    const basedOn = (document.provenance as Record<string, unknown> | undefined)?.basedOn;
    return {
      dossierRef: dossierId,
      authorName: this.draftAuthor(),
      origin,
      basedOnVersion,
      basedOn: typeof basedOn === 'string' ? basedOn : null,
      spec: document,
      checksum: await moduleSpecChecksum(document),
      validation,
      layout,
      createdAt,
      updatedAt: nowIso(),
    };
  }

  async getModuleSpecDraft(dossierId: string): Promise<ModuleSpecDraft | null> {
    this.requireDossier(dossierId);
    const draft = this.state.moduleSpecDrafts[dossierId];
    return draft ? clone(draft) : null;
  }

  /** Règles de `PUT …/draft` (rest §4.3) dans l'ordre : forme, bornes, layout, baseChecksum, écriture si changement. */
  async putModuleSpecDraft(dossierId: string, input: ModuleSpecDraftInput): Promise<ModuleSpecDraft> {
    this.requireDossier(dossierId);
    const spec = this.checkDraftShape(input?.spec);
    this.checkDraftLimits(spec);
    const existing = this.state.moduleSpecDrafts[dossierId] ?? null;
    const layout = input?.layout === undefined ? (existing?.layout ?? { entities: {} }) : this.cleanLayout(spec, input.layout);
    const base = input?.baseChecksum ?? null;
    if (!existing) {
      if (base !== null) throw designError('modulespec.draft.conflict', '');
    } else if (base !== existing.checksum) {
      throw designError('modulespec.draft.conflict', existing.checksum);
    }
    const checksum = await moduleSpecChecksum(spec);
    if (existing && existing.checksum === checksum && canonicalJson(existing.layout) === canonicalJson(layout)) {
      return clone(existing);
    }
    const draft = await this.buildDraft(dossierId, spec, existing?.origin ?? 'empty', existing?.basedOnVersion ?? null, layout, existing?.createdAt ?? nowIso());
    this.state.moduleSpecDrafts[dossierId] = draft;
    this.persist();
    return clone(draft);
  }

  /** 404 modulespec.not_found · 409 modulespec.draft.exists ; copie de la version (origin `version`). */
  async createDraftFromVersion(dossierId: string, version: number, archiveChecksum?: string): Promise<ModuleSpecDraft> {
    this.requireDossier(dossierId);
    const source = this.requireModuleSpecVersion(dossierId, version);
    const previous = this.state.moduleSpecDrafts[dossierId];
    if (previous && !archiveChecksum) throw designError('modulespec.draft.exists');
    if (archiveChecksum && previous?.checksum !== archiveChecksum) throw designError('modulespec.draft.conflict');
    const draft = await this.buildDraft(dossierId, source.spec as Record<string, unknown>, 'version', version, { entities: {} }, nowIso());
    this.state.moduleSpecDrafts[dossierId] = draft;
    this.persist();
    return clone(draft);
  }

  /** 404 modulespec.draft.not_found ; règles `propose` de UPT-2095 (`human`), puis suppression du brouillon. L10b : `feedbackIds` est ignoré (les retours de démonstration sont figés, aucune écriture). */
  async proposeModuleSpecDraft(dossierId: string, input: ModuleSpecDraftProposeInput): Promise<ModuleSpecVersion> {
    this.requireDossier(dossierId);
    const draft = this.state.moduleSpecDrafts[dossierId];
    if (!draft) throw designError('modulespec.draft.not_found');
    const comment = typeof input?.comment === 'string' ? input.comment.trim() : '';
    if (comment.length > MODULE_SPEC_COMMENT_MAX) throw designError('modulespec.invalid', 'comment');
    const created = await this.proposeModuleSpecVersion(dossierId, { spec: draft.spec, source: 'human' });
    delete this.state.moduleSpecDrafts[dossierId];
    this.persist();
    return created;
  }

  async deleteModuleSpecDraft(dossierId: string): Promise<void> {
    this.requireDossier(dossierId);
    if (!this.state.moduleSpecDrafts[dossierId]) throw designError('modulespec.draft.not_found');
    delete this.state.moduleSpecDrafts[dossierId];
    this.persist();
  }

  /** Seul `pret-materiel` est « installé » en démonstration ; tout autre code → 404 modulespec.export.module_not_found. */
  private exportedModuleSpec(moduleCode: string): Record<string, unknown> {
    if (moduleCode !== DEMO_EXPORT_MODULE) throw designError('modulespec.export.module_not_found');
    const spec = clone(pretMaterielFixture) as Record<string, unknown>;
    spec.specVersion = '1.1';
    const provenance = (spec.provenance && typeof spec.provenance === 'object' ? spec.provenance : {}) as Record<string, unknown>;
    spec.provenance = { ...provenance, basedOn: `export ${DEMO_EXPORT_MODULE}@${DEMO_EXPORT_VERSION}` };
    return spec;
  }

  /** 409 modulespec.draft.exists AVANT la lecture (rest §4.5.5) ; brouillon `export`, `notes = []`. */
  async exportModuleToDraft(dossierId: string, moduleCode: string): Promise<ModuleSpecExportResult> {
    this.requireDossier(dossierId);
    if (this.state.moduleSpecDrafts[dossierId]) throw designError('modulespec.draft.exists');
    const spec = this.exportedModuleSpec(moduleCode);
    this.checkDraftLimits(spec);
    const draft = await this.buildDraft(dossierId, spec, 'export', null, { entities: {} }, nowIso());
    this.state.moduleSpecDrafts[dossierId] = draft;
    this.persist();
    return { draft: clone(draft), notes: [] };
  }

  async previewModuleExport(moduleCode: string): Promise<ModuleSpecExportPreview> {
    const spec = this.exportedModuleSpec(moduleCode);
    return { spec: spec as ModuleSpecDocument, validation: moduleSpecLocalCheck(spec), notes: [] };
  }

  /** Catalogue de démonstration : énumérations du schéma (constantes) + le module `pret-materiel`. */
  async getDesignerCatalog(): Promise<DesignerCatalog> {
    return clone(DEMO_CATALOG);
  }

  /* ── Preview (UPT-2079 L4, web §4.3, rest §4.3 à §4.8) ───────────────────────────────────────────── */

  private previewDatasetsOf(dossierId: string): PreviewStoredDataset[] {
    return this.state.previewDatasets[dossierId] ?? [];
  }

  private requirePreviewDataset(dossierId: string, datasetId: string): PreviewStoredDataset {
    const found = this.previewDatasetsOf(dossierId).find((entry) => entry.id === datasetId);
    if (!found) throw designError('preview.dataset.not_found');
    return found;
  }

  private newPreviewDatasetId(): string {
    if (this.options.newId) return this.options.newId();
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `pd-${randomSuffix()}${randomSuffix()}`;
  }

  /** Source d'un jeu : brouillon du dossier (404 `modulespec.draft.not_found`) ou version numérotée (404 `modulespec.not_found`). */
  private readPreviewSource(dossierId: string, source: PreviewSource): { spec: unknown; checksum: string } {
    if (source.kind === 'draft') {
      const draft = this.state.moduleSpecDrafts[dossierId];
      if (!draft) throw designError('modulespec.draft.not_found');
      return { spec: draft.spec, checksum: draft.checksum };
    }
    const version = this.requireModuleSpecVersion(dossierId, source.version ?? 0);
    return { spec: version.spec, checksum: version.checksum };
  }

  /** Projection de l'entité (400 `invalid` `entityCode` si absente ; 413 `too_large` `snapshot` au-delà de 256 Kio). */
  private projectPreviewEntity(spec: unknown, entityCode: string): PreviewSnapshot {
    const snapshot = buildPreviewSnapshot(spec, entityCode);
    if (!snapshot) throw designError('preview.dataset.invalid', 'entityCode');
    if (isPreviewSnapshotTooLarge(snapshot)) throw designError('preview.dataset.too_large', 'snapshot');
    return snapshot;
  }

  /**
   * Avertissements et empreinte recalculés (à chaque écriture). Comme le serveur réalisé (rapport rest L4, écart 5) :
   * l'acquittement est EFFACÉ dès que l'empreinte change (un retour à une liste identique ne le ressuscite pas).
   */
  private async applyPreviewWarnings(stored: PreviewStoredDataset): Promise<void> {
    stored.warnings = scanDatasetWarnings(stored.name, stored.snapshot, stored.rows);
    stored.warningsFingerprint = await warningsFingerprintOf(stored.warnings);
    if (stored.ack && stored.ack.sha !== stored.warningsFingerprint) {
      stored.ack = null;
    }
  }

  async listPreviewDatasets(dossierId: string): Promise<PreviewDatasetSummary[]> {
    this.requireDossier(dossierId);
    return this.previewDatasetsOf(dossierId)
      .map((entry, index) => ({ entry, index }))
      .sort((left, right) => right.entry.createdAt.localeCompare(left.entry.createdAt) || right.index - left.index)
      .map(({ entry }) => summarizePreviewDataset(entry));
  }

  async getPreviewDataset(dossierId: string, datasetId: string): Promise<PreviewDataset | null> {
    const found = this.previewDatasetsOf(dossierId).find((entry) => entry.id === datasetId);
    return found ? presentPreviewDataset(found) : null;
  }

  async createPreviewDataset(dossierId: string, input: PreviewDatasetCreateInput): Promise<PreviewDataset> {
    this.requireDossier(dossierId);
    const name = checkPreviewName(input?.name);
    const entityCode = typeof input?.entityCode === 'string' ? input.entityCode : '';
    if (!PREVIEW_ENTITY_CODE_PATTERN.test(entityCode)) throw designError('preview.dataset.invalid', 'entityCode');
    const source = checkPreviewSource(input?.source);
    if (this.previewDatasetsOf(dossierId).length >= PREVIEW_LIMITS.datasetsPerDossier) {
      throw designError('preview.dataset.limit', 'datasets');
    }
    if (input?.generate !== undefined && input?.rows !== undefined) throw designError('preview.dataset.invalid', 'rows');
    const generate = input?.generate === undefined ? null : checkGenerateInput(input.generate, null);
    const origin = this.readPreviewSource(dossierId, source);
    const snapshot = this.projectPreviewEntity(origin.spec, entityCode);
    // Les lignes générées passent aussi par la validation (normalisation et borne de 1 Mio : rapport rest L4, écart 7).
    const rows = generate
      ? normalizePreviewRows(generateRows(entityCode, snapshot.entity.fields, generate.count, generate.seed), snapshot)
      : input?.rows !== undefined
        ? normalizePreviewRows(input.rows, snapshot)
        : [];
    const now = nowIso();
    const stored: PreviewStoredDataset = {
      id: this.newPreviewDatasetId(),
      dossierId,
      name,
      entityCode,
      entityLabel: previewEntityLabel(snapshot),
      moduleCode: snapshot.module.code,
      source,
      specChecksum: origin.checksum,
      capturedAt: now,
      rowCount: rows.length,
      seed: generate?.seed ?? 1,
      revision: 1,
      hasWarnings: false,
      warningsAcknowledged: false,
      share: { active: false, suspended: false, expiresAt: null, createdBy: null, createdAt: null },
      createdBy: this.draftAuthor(),
      createdAt: now,
      updatedBy: null,
      updatedAt: null,
      snapshot,
      rows,
      warnings: [],
      warningsFingerprint: null,
      acknowledgedBy: null,
      acknowledgedAt: null,
      ack: null,
    };
    await this.applyPreviewWarnings(stored);
    this.state.previewDatasets[dossierId] = [...this.previewDatasetsOf(dossierId), stored];
    this.persist();
    return presentPreviewDataset(stored);
  }

  /** `baseRevision` entier égal à la révision (sinon 400 / 409 dont le message est la révision) ; aucun changement → aucune écriture. */
  async updatePreviewDataset(
    dossierId: string,
    datasetId: string,
    input: PreviewDatasetUpdateInput
  ): Promise<PreviewDataset> {
    const stored = this.requirePreviewDataset(dossierId, datasetId);
    const base = input?.baseRevision;
    if (!isInteger(base)) throw designError('preview.dataset.invalid', 'baseRevision');
    if (base !== stored.revision) throw designError('preview.dataset.conflict', String(stored.revision));
    const name = input.name === undefined ? stored.name : checkPreviewName(input.name);
    const rows = input.rows === undefined ? stored.rows : normalizePreviewRows(input.rows, stored.snapshot);
    if (name === stored.name && canonicalJson(rows) === canonicalJson(stored.rows)) {
      return presentPreviewDataset(stored);
    }
    stored.name = name;
    stored.rows = rows;
    stored.rowCount = rows.length;
    await this.applyPreviewWarnings(stored);
    stored.revision += 1;
    stored.updatedAt = nowIso();
    stored.updatedBy = this.draftAuthor();
    this.persist();
    return presentPreviewDataset(stored);
  }

  /** Remplace les lignes (graine par défaut : celle du jeu) ; `count = 0` vide le jeu ; révision + 1 sans condition. */
  async generatePreviewRows(dossierId: string, datasetId: string, input: PreviewGenerateInput): Promise<PreviewDataset> {
    const stored = this.requirePreviewDataset(dossierId, datasetId);
    const { count, seed } = checkGenerateInput(input, stored.seed);
    stored.rows = normalizePreviewRows(generateRows(stored.entityCode, stored.snapshot.entity.fields, count, seed), stored.snapshot);
    stored.rowCount = stored.rows.length;
    stored.seed = seed;
    await this.applyPreviewWarnings(stored);
    stored.revision += 1;
    stored.updatedAt = nowIso();
    stored.updatedBy = this.draftAuthor();
    this.persist();
    return presentPreviewDataset(stored);
  }

  /** Relit la source, reprojette l'entité du jeu ; les clés de lignes qui ne sont plus des champs sont retirées (`droppedKeys`). */
  async refreshPreviewSnapshot(
    dossierId: string,
    datasetId: string,
    input: PreviewRefreshInput
  ): Promise<PreviewRefreshResult> {
    const stored = this.requirePreviewDataset(dossierId, datasetId);
    const source = checkPreviewSource(input?.source);
    const origin = this.readPreviewSource(dossierId, source);
    const snapshot = this.projectPreviewEntity(origin.spec, stored.entityCode);
    const names = new Set(snapshot.entity.fields.map((field) => field.name));
    const dropped = new Set<string>();
    stored.rows = stored.rows.map((row) => {
      const kept: PreviewRow = { _id: row._id };
      Object.keys(row).forEach((key) => {
        if (key === '_id') return;
        if (names.has(key)) kept[key] = row[key];
        else dropped.add(key);
      });
      return kept;
    });
    stored.snapshot = snapshot;
    stored.entityLabel = previewEntityLabel(snapshot);
    stored.moduleCode = snapshot.module.code;
    stored.source = source;
    stored.specChecksum = origin.checksum;
    stored.capturedAt = nowIso();
    await this.applyPreviewWarnings(stored);
    stored.revision += 1;
    stored.updatedAt = nowIso();
    stored.updatedBy = this.draftAuthor();
    this.persist();
    return { dataset: presentPreviewDataset(stored), droppedKeys: [...dropped].sort() };
  }

  /** Empreinte absente, vide ou différente de la courante (ou aucun avertissement) → 409 `preview.dataset.warnings_changed`. */
  async acknowledgePreviewWarnings(
    dossierId: string,
    datasetId: string,
    input: PreviewAcknowledgeInput
  ): Promise<PreviewDataset> {
    const stored = this.requirePreviewDataset(dossierId, datasetId);
    const received = input?.warningsFingerprint;
    if (typeof received !== 'string' || received.length === 0 || stored.warningsFingerprint === null || received !== stored.warningsFingerprint) {
      throw designError('preview.dataset.warnings_changed');
    }
    // Comme le serveur (rapport rest L4, écart 6) : l'acquittement est une écriture, la révision augmente.
    stored.ack = { sha: stored.warningsFingerprint, by: this.draftAuthor(), at: nowIso() };
    stored.revision += 1;
    stored.updatedAt = stored.ack.at;
    stored.updatedBy = stored.ack.by;
    this.persist();
    return presentPreviewDataset(stored);
  }

  async deletePreviewDataset(dossierId: string, datasetId: string): Promise<void> {
    this.requirePreviewDataset(dossierId, datasetId);
    this.state.previewDatasets[dossierId] = this.previewDatasetsOf(dossierId).filter((entry) => entry.id !== datasetId);
    this.persist();
  }

  /** Aucun lien en démonstration (le lien se lit sur le serveur). */
  createPreviewShare(_dossierId: string, _datasetId: string, _input: PreviewShareInput): Promise<PreviewShareCreated> {
    return Promise.reject(new NotServedError('preview', 'createPreviewShare'));
  }

  revokePreviewShare(_dossierId: string, _datasetId: string): Promise<void> {
    return Promise.reject(new NotServedError('preview', 'revokePreviewShare'));
  }

  // ── Générateur (UPT-2079 L5, web §4.3) ───────────────────────────────────────

  private generationRunsOf(dossierId: string): GeneratorRun[] {
    return this.state.generationRuns[dossierId] ?? [];
  }

  /** Résumé d'une exécution : tous les champs sauf le plan et l'état de la cible du dépôt (comme `GET …/generation-runs`). */
  private summarizeGenerationRun(run: GeneratorRun): GeneratorRunSummary {
    const summary: Partial<GeneratorRun> = { ...run };
    delete summary.plan;
    delete summary.repoStatus;
    return summary as GeneratorRunSummary;
  }

  /** Du plus récent au plus ancien ; dossier inconnu → 404 `dossier.not_found`. */
  listGenerationRuns(dossierId: string): Promise<GeneratorRunSummary[]> {
    // L'exécuteur d'une promesse transforme `dossier.not_found` en rejet, sans `async` ni `await`.
    return new Promise((resolve) => {
      this.requireDossier(dossierId);
      resolve(
        clone(
          [...this.generationRunsOf(dossierId)]
            .sort((left, right) => right.startedAt.localeCompare(left.startedAt))
            .map((run) => this.summarizeGenerationRun(run))
        )
      );
    });
  }

  getGenerationRun(dossierId: string, runId: string): Promise<GeneratorRun | null> {
    const found = this.generationRunsOf(dossierId).find((entry) => entry.id === runId);
    return Promise.resolve(found ? clone(found) : null);
  }

  /** Le serveur seul sait produire le bundle. */
  suggestedMigrationDate(): Promise<string> {
    return Promise.reject(new NotServedError('generator', 'suggestedMigrationDate'));
  }

  createGenerationRun(): Promise<GeneratorRun> {
    return Promise.reject(new NotServedError('generator', 'createGenerationRun'));
  }

  /** La démonstration est en lecture seule : l'exécution d'exemple ne se supprime pas. */
  deleteGenerationRun(): Promise<void> {
    return Promise.reject(new NotServedError('generator', 'deleteGenerationRun'));
  }

  readGeneratorFile(): Promise<string> {
    return Promise.reject(new NotServedError('generator', 'readGeneratorFile'));
  }

  downloadGeneratorBundle(): Promise<Blob> {
    return Promise.reject(new NotServedError('generator', 'downloadGeneratorBundle'));
  }

  runGeneratorStripe(): Promise<GeneratorStripeResult> {
    return Promise.reject(new NotServedError('generatorStripe', 'runGeneratorStripe'));
  }

  /** Aucun dépôt git en démonstration : le serveur seul écrit dans le dépôt du module. */
  depositGeneratorRun(): Promise<GeneratorRepoDeposit> {
    return Promise.reject(new NotServedError('generatorRepo', 'depositGeneratorRun'));
  }

  // ── Pipeline (UPT-2079 L6a, web §4.3) : lecture du jeu figé, aucune écriture ───────────────────────────

  getPipelineEnvironments(): Promise<PipelineEnvironmentsPayload> {
    return Promise.resolve(clone(this.pipeline.environments));
  }

  /** Les locataires fictifs du jeu (le tiroir du registre reste lisible en démonstration, inactif). */
  listPipelineTenants(): Promise<PipelineTenantOption[]> {
    return Promise.resolve(clone(this.pipeline.tenants));
  }

  /** Du plus récent au plus ancien ; `env` filtre `toEnv` ; `limit` borne la liste (défaut 30). */
  listPipelineRuns(filter?: { kind?: PipelineKind; env?: EnvironmentCode; moduleCode?: string; limit?: number }): Promise<PipelineRunSummary[]> {
    const limit = typeof filter?.limit === 'number' ? filter.limit : PIPELINE_LIST_DEFAULT;
    const runs = this.pipeline.runs
      .filter((run) => (filter?.kind ? run.kind === filter.kind : true))
      .filter((run) => (filter?.env ? run.toEnv === filter.env : true))
      .filter((run) => (filter?.moduleCode ? run.moduleCode === filter.moduleCode : true))
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
      .slice(0, limit)
      .map(summarizePipelineRun);
    return Promise.resolve(clone(runs));
  }

  getPipelineRun(id: string): Promise<PipelineRun | null> {
    const found = this.pipeline.runs.find((run) => run.id === id);
    return Promise.resolve(found ? clone(found) : null);
  }

  /** La démonstration ne relève rien : le serveur seul lit la version déployée et les pipelines CD. */
  readPipelineEnvironment(): Promise<PipelineRun> {
    return Promise.reject(new NotServedError('pipeline', 'readPipelineEnvironment'));
  }

  updatePipelineEnvironment(_code: EnvironmentCode, _input: PipelineEnvironmentInput): Promise<PipelineEnvironment> {
    return Promise.reject(new NotServedError('pipeline', 'updatePipelineEnvironment'));
  }

  deletePipelineRun(): Promise<void> {
    return Promise.reject(new NotServedError('pipeline', 'deletePipelineRun'));
  }

  /** La démonstration ne copie rien : le serveur seul écrit dans la base d'un environnement (ressource `pipelineData`). */
  planPipelinePromotion(): Promise<PipelineRun> {
    return Promise.reject(new NotServedError('pipelineData', 'planPipelinePromotion'));
  }

  executePipelineRun(): Promise<PipelineRun> {
    return Promise.reject(new NotServedError('pipelineData', 'executePipelineRun'));
  }

  rollbackPipelineRun(): Promise<PipelineRun> {
    return Promise.reject(new NotServedError('pipelineData', 'rollbackPipelineRun'));
  }

  /** La démonstration n'évalue ni ne lit rien de réel : l'approbation, les conditions « tests » et le parcours sont ceux du serveur (ressource `pipelineGates`). */
  approvePipelineRun(): Promise<PipelineRun> {
    return Promise.reject(new NotServedError('pipelineGates', 'approvePipelineRun'));
  }

  getPipelineGates(): Promise<PipelineGateTests> {
    return Promise.reject(new NotServedError('pipelineGates', 'getPipelineGates'));
  }

  getPipelineJourney(): Promise<PipelineJourney> {
    return Promise.reject(new NotServedError('pipelineGates', 'getPipelineJourney'));
  }

  openPipelineEnvironment(): Promise<PipelineLicenseResult> {
    return Promise.reject(new NotServedError('pipelineGates', 'openPipelineEnvironment'));
  }

  // ── Retours de recette (UPT-2079 L10a, web §4.3) : lecture du jeu figé, aucune écriture ────────────────

  /**
   * Du plus récent au plus ancien. `dossierId` et `moduleCode` bornent le périmètre des compteurs ; `runId`, `stepId`, `status`, `category`
   * (`none` = pas encore classé) et `routedTo` ne filtrent que la liste ; `limit` 1 à 200 (défaut 50), `offset` ≥ 0.
   */
  listFeedback(filter?: FeedbackFilter): Promise<FeedbackPage> {
    const scope = this.feedback.items
      .filter((item) => (filter?.dossierId ? item.dossierId === filter.dossierId : true))
      .filter((item) => (filter?.moduleCode ? item.moduleCode === filter.moduleCode : true));
    const matching = scope
      .filter((item) => (filter?.runId ? item.sourceRunId === filter.runId : true))
      .filter((item) => (filter?.stepId ? item.sourceStepId === filter.stepId : true))
      .filter((item) => (filter?.status && filter.status.length > 0 ? filter.status.includes(item.status) : true))
      .filter((item) => (filter?.category && filter.category.length > 0 ? filter.category.includes(item.category ?? 'none') : true))
      .filter((item) => (filter?.routedTo ? item.route === filter.routedTo : true))
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt) || right.number - left.number);
    const limit = Math.min(Math.max(typeof filter?.limit === 'number' ? filter.limit : FEEDBACK_LIMITS.listDefault, 1), FEEDBACK_LIMITS.listMax);
    const offset = Math.max(typeof filter?.offset === 'number' ? filter.offset : 0, 0);
    return Promise.resolve(
      clone({ items: matching.slice(offset, offset + limit).map(summarizeFeedback), total: matching.length, counts: countFeedback(scope) })
    );
  }

  /** Le détail avec son journal ; un identifiant inconnu → `null` (comme le 404 `feedback.not_found` du dépôt REST). */
  getFeedback(id: string): Promise<Feedback | null> {
    const found = this.feedback.items.find((item) => item.id === id);
    return Promise.resolve(found ? clone(found) : null);
  }

  /** La démonstration ne saisit, ne modifie, ne classe, ne transmet, ne résout, n'écarte et ne rouvre rien : le serveur seul tient le journal. */
  createFeedback(): Promise<Feedback> {
    return Promise.reject(new NotServedError('feedback', 'createFeedback'));
  }

  updateFeedback(): Promise<Feedback> {
    return Promise.reject(new NotServedError('feedback', 'updateFeedback'));
  }

  classifyFeedback(): Promise<FeedbackSummary[]> {
    return Promise.reject(new NotServedError('feedback', 'classifyFeedback'));
  }

  transmitFeedback(): Promise<FeedbackSummary[]> {
    return Promise.reject(new NotServedError('feedback', 'transmitFeedback'));
  }

  getFeedbackTransmission(): Promise<FeedbackTransmission> {
    return Promise.reject(new NotServedError('feedback', 'getFeedbackTransmission'));
  }

  resolveFeedback(): Promise<FeedbackSummary[]> {
    return Promise.reject(new NotServedError('feedback', 'resolveFeedback'));
  }

  rejectFeedback(): Promise<FeedbackSummary[]> {
    return Promise.reject(new NotServedError('feedback', 'rejectFeedback'));
  }

  reopenFeedback(): Promise<FeedbackSummary[]> {
    return Promise.reject(new NotServedError('feedback', 'reopenFeedback'));
  }

  // ── Specs ─────────────────────────────────────────────────────────────────

  /** Tri du serveur : `kind` dans l'ordre rest · web · shared · report, puis `path`. */
  async listSpecs(dossierId: string): Promise<SpecDocument[]> {
    return clone(
      this.state.specs
        .filter((spec) => spec.dossierId === dossierId)
        .sort(
          (left, right) =>
            KIND_ORDER.indexOf(left.kind) - KIND_ORDER.indexOf(right.kind) ||
            left.path.localeCompare(right.path)
        )
    );
  }

  private versionsOf(documentId: string): DesignSpecVersion[] {
    return this.state.specVersions[documentId] ?? [];
  }

  private requireSpec(id: string): DesignSpecDocument {
    const spec = this.state.specs.find((candidate) => candidate.id === id);
    if (!spec) {
      throw designError('spec.not_found');
    }
    return spec;
  }

  private toDetail(spec: DesignSpecDocument): DesignSpecDetail {
    const versions = this.versionsOf(spec.id);
    const current = versions.find((version) => version.version === spec.currentVersion) ?? null;
    return { ...clone(spec), current: current ? clone(current) : null };
  }

  private newSpecId(): string {
    return this.newPieceId();
  }

  /** Nouvelle version immuable (`currentVersion + 1`) et mise à jour du document. */
  private async appendVersion(
    spec: DesignSpecDocument,
    normalized: string,
    source: DesignSpecVersionSource,
    message: string | null,
    checksum?: string
  ): Promise<DesignSpecVersion> {
    const version: DesignSpecVersion = {
      id: this.newSpecId(),
      documentId: spec.id,
      version: spec.currentVersion + 1,
      checksum: checksum ?? (await checksumOf(normalized)),
      source,
      repoCommit: null,
      message,
      sizeBytes: utf8Bytes(normalized),
      lineCount: lineCount(normalized),
      createdBy: this.options.currentUserName?.() ?? '',
      createdAt: nowIso(),
      contentMd: normalized,
    };
    this.state.specVersions[spec.id] = [...this.versionsOf(spec.id), version];
    spec.currentVersion = version.version;
    spec.currentVersionId = version.id;
    spec.checksum = version.checksum;
    spec.lastSource = source;
    spec.updatedAt = version.createdAt;
    return version;
  }

  async getSpec(id: string): Promise<DesignSpecDetail | null> {
    const spec = this.state.specs.find((candidate) => candidate.id === id);
    return spec ? this.toDetail(spec) : null;
  }

  async createSpec(dossierId: string, input: DesignSpecDocumentInput): Promise<DesignSpecDetail> {
    this.requireDossier(dossierId);
    const normalized = normalizeSpecInput(input);
    if (this.state.specs.some((candidate) => candidate.dossierId === dossierId && candidate.path === normalized.path)) {
      throw designError('spec.path.taken');
    }
    if (normalized.contentMd !== undefined && isSpecContentTooLarge(normalized.contentMd)) {
      throw designError('spec.too_large');
    }
    const now = nowIso();
    const spec: DesignSpecDocument = {
      id: this.newSpecId(),
      dossierId,
      kind: normalized.kind,
      title: normalized.title,
      path: normalized.path,
      status: normalized.status,
      assignee: normalized.assignee,
      ticketRef: normalized.ticketRef,
      currentVersion: 0,
      currentVersionId: null,
      checksum: null,
      lastSource: null,
      repoCommit: null,
      syncedAt: null,
      createdAt: now,
      updatedAt: now,
    };
    this.state.specs.push(spec);
    if (normalized.contentMd !== undefined) {
      await this.appendVersion(spec, normalized.contentMd, 'human', normalized.message);
    }
    this.persist();
    return this.toDetail(spec);
  }

  async updateSpec(id: string, input: DesignSpecDocumentInput): Promise<DesignSpecDetail> {
    const spec = this.requireSpec(id);
    const normalized = normalizeSpecInput(input);
    if (
      normalized.path !== spec.path &&
      this.state.specs.some(
        (candidate) => candidate.dossierId === spec.dossierId && candidate.path === normalized.path && candidate.id !== id
      )
    ) {
      throw designError('spec.path.taken');
    }
    if (normalized.contentMd !== undefined) {
      if (input.baseVersion === undefined || input.baseVersion === null) {
        throw designError('spec.invalid', 'baseVersion');
      }
      if (input.baseVersion !== spec.currentVersion) {
        throw designError('spec.version.conflict', String(spec.currentVersion));
      }
      if (isSpecContentTooLarge(normalized.contentMd)) {
        throw designError('spec.too_large');
      }
    }
    spec.kind = normalized.kind;
    spec.title = normalized.title;
    spec.path = normalized.path;
    spec.status = normalized.status;
    spec.assignee = normalized.assignee;
    spec.ticketRef = normalized.ticketRef;
    if (normalized.contentMd !== undefined) {
      const checksum = await checksumOf(normalized.contentMd);
      if (checksum !== spec.checksum) {
        await this.appendVersion(spec, normalized.contentMd, 'human', normalized.message, checksum);
      }
    }
    spec.updatedAt = nowIso();
    this.persist();
    return this.toDetail(spec);
  }

  async listSpecVersions(id: string): Promise<DesignSpecVersionSummary[]> {
    this.requireSpec(id);
    return [...this.versionsOf(id)]
      .sort((left, right) => right.version - left.version)
      .map((version) => {
        const { contentMd: _content, ...summary } = version;
        return clone(summary);
      });
  }

  async getSpecVersion(id: string, version: number): Promise<DesignSpecVersion> {
    this.requireSpec(id);
    const found = Number.isInteger(version) && version >= 1
      ? this.versionsOf(id).find((candidate) => candidate.version === version)
      : undefined;
    if (!found) {
      throw designError('spec.version.not_found');
    }
    return clone(found);
  }

  /** Bornes du serveur (rest §4.5) : `to` absent → courante, `from` absent → `to` − 1, `from` = 0 → contenu vide. */
  async diffSpec(id: string, from: number | null, to: number | null): Promise<DesignSpecDiff> {
    const spec = this.requireSpec(id);
    if (spec.currentVersion === 0) {
      throw designError('spec.diff.invalid');
    }
    const toVersion = to ?? spec.currentVersion;
    const fromVersion = from ?? toVersion - 1;
    if (
      !Number.isInteger(fromVersion) ||
      !Number.isInteger(toVersion) ||
      fromVersion < 0 ||
      toVersion < 1 ||
      toVersion > spec.currentVersion ||
      fromVersion >= toVersion
    ) {
      throw designError('spec.diff.invalid');
    }
    const fromText = fromVersion === 0 ? '' : (await this.getSpecVersion(id, fromVersion)).contentMd;
    const toText = (await this.getSpecVersion(id, toVersion)).contentMd;
    const result = unifiedDiff(fromText, toText, fromVersion, toVersion);
    return { documentId: id, from: fromVersion, to: toVersion, ...result };
  }

  /** Squelette du gabarit sans IA (rest §4.6) depuis les données du jeu ; version `agent`, toujours créée. */
  async generateSpec(id: string, _input: DesignSpecGenerateInput): Promise<DesignSpecDetail> {
    const spec = this.requireSpec(id);
    const dossier = this.state.dossiers.find((candidate) => candidate.id === spec.dossierId);
    if (!dossier) {
      throw designError('spec.not_found');
    }
    const prototypes: SkeletonPrototype[] = [...this.prototypeImports.values()]
      .map((entry) => entry.record)
      .filter((record) => record.dossierRef === dossier.id && record.status !== 'archived')
      .map((record) => ({
        title: record.title,
        toolName: record.toolName,
        pieceCount: record.pieces.length,
        inventory: record.inventory,
      }));
    const counterpartKind: DesignSpecKind | null =
      spec.kind === 'rest' ? 'web' : spec.kind === 'web' ? 'rest' : null;
    const counterpart =
      counterpartKind && spec.ticketRef
        ? this.state.specs.find(
            (candidate) =>
              candidate.dossierId === spec.dossierId &&
              candidate.id !== spec.id &&
              candidate.kind === counterpartKind &&
              candidate.ticketRef === spec.ticketRef
          )
        : undefined;
    let markdown: string;
    try {
      markdown = buildSpecSkeleton({
        dossier,
        document: spec,
        moduleSpec: null,
        prototypes,
        counterpartPath: counterpart?.path ?? null,
        date: formatSkeletonDate(new Date()),
      });
    } catch {
      throw designError('spec.generate.failed');
    }
    await this.appendVersion(spec, markdown, 'agent', SKELETON_MESSAGE);
    this.persist();
    return this.toDetail(spec);
  }

  /** La démonstration n'a pas de dépôt git : toujours 409 spec.sync.unavailable / `no-config`. */
  async syncSpec(id: string, _input: DesignSpecSyncInput): Promise<DesignSpecSyncResult> {
    this.requireSpec(id);
    throw designError('spec.sync.unavailable', 'no-config');
  }

  // ── Plans de test ─────────────────────────────────────────────────────────

  async listTestPlans(filter?: { dossierId?: string; moduleCode?: string }): Promise<TestPlanSummary[]> {
    return this.state.plans
      .filter((plan) => (filter?.dossierId ? plan.dossierId === filter.dossierId : true))
      .filter((plan) => (filter?.moduleCode ? plan.moduleCode === filter.moduleCode : true))
      .map((plan) => this.summarizePlan(plan));
  }

  async getTestPlan(id: string): Promise<TestPlan | null> {
    const plan = this.state.plans.find((candidate) => candidate.id === id);
    return plan ? clone(plan) : null;
  }

  async saveTestPlan(plan: TestPlan): Promise<TestPlan> {
    const index = this.state.plans.findIndex((candidate) => candidate.id === plan.id);
    const copy = clone(plan);
    if (index >= 0) {
      this.state.plans[index] = copy;
    } else {
      this.state.plans.push(copy);
    }
    this.persist();
    return clone(copy);
  }

  // ── Exécutions ────────────────────────────────────────────────────────────

  async listTestRuns(planId: string): Promise<TestRunSummary[]> {
    return this.state.runs
      .filter((run) => run.planId === planId)
      .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
      .map((run) => this.summarizeRun(run));
  }

  async getTestRun(id: string): Promise<TestRun | null> {
    const run = this.state.runs.find((candidate) => candidate.id === id);
    return run ? clone(run) : null;
  }

  async createTestRun(input: {
    planId: string;
    environmentCode: EnvironmentCode;
    testerName: string;
  }): Promise<TestRun> {
    const created: TestRun = {
      id: `tr-${randomSuffix()}`,
      planId: input.planId,
      environmentCode: input.environmentCode,
      testerKind: 'human',
      testerName: input.testerName,
      status: 'open',
      startedAt: nowIso(),
      closedAt: null,
      results: {},
      notes: {},
      reportMd: null,
      paperclipRef: null,
      changeRequestRef: null,
    };
    this.state.runs.push(created);
    this.persist();
    return clone(created);
  }

  private requireRun(runId: string): TestRun {
    const run = this.state.runs.find((candidate) => candidate.id === runId);
    if (!run) {
      throw new Error(`module-studio: exécution introuvable (${runId})`);
    }
    return run;
  }

  async putTestResult(runId: string, stepId: string, input: TestResultInput): Promise<TestResult> {
    const run = this.requireRun(runId);
    if (
      (input.verdict === 'fail' || input.verdict === 'blocked') &&
      !(input.noteMd ?? '').trim()
    ) {
      throw new Error('module-studio: une note est obligatoire pour un échec ou un blocage');
    }
    const result: TestResult = {
      stepId,
      verdict: input.verdict,
      noteMd: input.noteMd ?? null,
      proposedVerdict: null,
      proposedByAi: false,
      evidence: [],
      validatedBy: null,
      validatedAt: null,
      updatedAt: nowIso(),
    };
    run.results[stepId] = result;
    this.persist();
    return clone(result);
  }

  async clearTestResult(runId: string, stepId: string): Promise<void> {
    const run = this.requireRun(runId);
    delete run.results[stepId];
    this.persist();
  }

  async validateProposedResult(
    runId: string,
    stepId: string,
    decision: 'accept' | 'reject'
  ): Promise<TestResult> {
    const run = this.requireRun(runId);
    const result = run.results[stepId];
    if (!result || result.verdict !== 'proposed') {
      throw new Error(`module-studio: aucune proposition à valider sur l'étape ${stepId}`);
    }
    if (decision === 'reject') {
      delete run.results[stepId];
      this.persist();
      return clone(result);
    }
    result.verdict = result.proposedVerdict ?? 'pass';
    result.validatedBy = this.options.currentUserName?.() ?? 'local';
    result.validatedAt = nowIso();
    result.updatedAt = nowIso();
    this.persist();
    return clone(result);
  }

  async putSceneNote(runId: string, sceneId: string, noteMd: string): Promise<void> {
    const run = this.requireRun(runId);
    if (noteMd.trim().length === 0) {
      delete run.notes[sceneId];
    } else {
      run.notes[sceneId] = noteMd;
    }
    this.persist();
  }

  async closeTestRun(
    runId: string,
    input: { reportMd: string; paperclipRef: string | null }
  ): Promise<TestRun> {
    const run = this.requireRun(runId);
    run.status = 'closed';
    run.closedAt = nowIso();
    run.reportMd = input.reportMd;
    run.paperclipRef = input.paperclipRef;
    this.persist();
    return clone(run);
  }

  /* ── Imports de prototypes de démonstration (UPT-2096 P1-a, §3.5) ──────
   * Vivent dans une Map privée HORS de FixtureState : jamais lus par
   * readStorage, jamais écrits par persist()/writeStorage, perdus au
   * rechargement. Le dépôt imite le serveur (droits, avertissements, noms
   * d'instantané). P1-b : `acknowledgePrototypeWarnings` imite le serveur
   * (empreinte ≠ courante ou courante null → 409 prototype.warnings_changed) ;
   * toutes les autres méthodes P1-b lèvent NotServedError('prototypeAnalysis')
   * (la démonstration ne sert jamais l'analyse). */
  private readonly prototypeImports = new Map<string, DemoPrototypeImport>();

  /* ── Habillage du module (UPT-2079 L14a) : ressources EN MÉMOIRE (octets, jamais en `localStorage`) et habillage calculé par le miroir de l'extracteur ── */
  private readonly assets = new DemoAssetStore();

  /** Numéros des versions `proposed` / `accepted` du dossier qui citent la ressource (`presentation.assets[].sha256`). */
  private assetUsedBy(dossierId: string, sha256: string): number[] {
    return this.moduleSpecVersionsOf(dossierId)
      .filter((entry) => entry.status === 'proposed' || entry.status === 'accepted')
      .filter((entry) => {
        const presentation = (entry.spec as Record<string, unknown>).presentation as { assets?: { sha256?: string }[] } | undefined;
        return (presentation?.assets ?? []).some((asset) => asset.sha256 === sha256);
      })
      .map((entry) => entry.version);
  }

  async listModuleAssets(dossierId: string): Promise<ModuleAsset[]> {
    this.requireDossier(dossierId);
    return this.assets.list(dossierId).map((asset) => ({ ...asset, usedBy: this.assetUsedBy(dossierId, asset.sha256) }));
  }

  async uploadModuleAsset(dossierId: string, image: Blob, metadata: ModuleAssetInput): Promise<ModuleAsset> {
    this.requireDossier(dossierId);
    if (metadata.origin === 'prototype-embedded') {
      const entry = metadata.importId ? this.prototypeImports.get(metadata.importId) : undefined;
      const piece = entry?.record.pieces.find((candidate) => candidate.id === metadata.pieceId);
      if (!entry || entry.record.dossierRef !== dossierId || !piece || (piece.kind !== 'html' && piece.kind !== 'snapshot')) {
        throw FixtureRepository.httpError(404, 'prototype.not_found');
      }
    }
    const bytes = await blobToBytes(image);
    const added = this.assets.add(dossierId, bytes, {
      origin:
        metadata.origin === 'prototype-embedded'
          ? { kind: 'prototype-embedded', importRef: metadata.importId, pieceRef: metadata.pieceId }
          : { kind: 'upload' },
      label: metadata.label ? metadata.label.slice(0, 120) : null,
      rightsAttested: metadata.rightsAttested === true,
      attestedBy: this.options.currentUserName?.() ?? 'demo',
      now: nowIso(),
    });
    return { ...added, usedBy: this.assetUsedBy(dossierId, added.sha256) };
  }

  async copyModuleAssetFromPiece(dossierId: string, input: ModuleAssetFromPieceInput): Promise<ModuleAsset> {
    this.requireDossier(dossierId);
    const entry = this.prototypeImports.get(input.importId);
    const blob = entry?.images.get(input.pieceId);
    if (!entry || entry.record.dossierRef !== dossierId || !blob) throw FixtureRepository.httpError(404, 'prototype.not_found');
    const piece = entry.record.pieces.find((candidate) => candidate.id === input.pieceId);
    const added = this.assets.add(dossierId, await blobToBytes(blob), {
      origin: { kind: 'prototype-image', importRef: input.importId, pieceRef: input.pieceId },
      label: piece ? piece.fileName.slice(0, 120) : null,
      // L'attestation de l'import est reprise (rest §4.8) : un import n'existe qu'attesté.
      rightsAttested: true,
      attestedBy: entry.record.createdBy,
      now: nowIso(),
    });
    return { ...added, usedBy: this.assetUsedBy(dossierId, added.sha256) };
  }

  async getModuleAssetBytes(dossierId: string, sha256: string): Promise<ArrayBuffer> {
    const bytes = this.assets.bytes(dossierId, sha256);
    if (!bytes) throw FixtureRepository.httpError(404, 'asset.not_found');
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  }

  async deleteModuleAsset(dossierId: string, sha256: string): Promise<void> {
    this.assets.remove(dossierId, sha256, this.assetUsedBy(dossierId, sha256));
  }

  /** Habillage calculé depuis le style mesuré de la première source (miroir de l'extracteur du serveur), aucune écriture. */
  async getPrototypePresentation(importId: string): Promise<PrototypePresentationResponse> {
    const entry = this.prototypeImports.get(importId);
    if (!entry) throw FixtureRepository.httpError(404, 'prototype.not_found');
    const source = [...(entry.record.inventory?.sources ?? [])]
      .sort((a, b) => a.sourceIndex - b.sourceIndex)
      .find((candidate) => candidate.style != null);
    if (!source || !source.style) {
      return { presentation: null, remarks: [{ code: 'presentation.no_style', path: '/presentation', message: '' }], sourceIndex: null, resolved: null };
    }
    const extracted = presentationFromSummary(source.style);
    return {
      presentation: extracted.presentation,
      remarks: extracted.remarks,
      sourceIndex: source.sourceIndex,
      resolved: extracted.presentation ? resolvePresentation(extracted.presentation) : null,
    };
  }

  private newPieceId(): string {
    if (this.options.newId) return this.options.newId();
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `pz-${randomSuffix()}`;
  }

  private demoOrganisation(): string {
    return this.options.demoOrganisation?.() ?? 'Organisation de démonstration';
  }

  private static demoSha(pieceId: string): string {
    // Empreinte de démonstration (forme seulement) : jamais une valeur personnelle.
    let hex = '';
    for (let i = 0; i < pieceId.length && hex.length < 64; i += 1) {
      hex += pieceId.charCodeAt(i).toString(16).padStart(2, '0');
    }
    return (hex + '0'.repeat(64)).slice(0, 64);
  }

  private static demoFileName(original: string, kind: 'html' | 'image' | 'markdown', sortOrder: number): string {
    const extension = kind === 'image' ? '.png' : kind === 'markdown' ? '.md' : '.html';
    let base = (original ?? '').split(/[\\/]/).pop() ?? '';
    base = base.normalize('NFKD').replace(/\p{M}/gu, '');
    const dot = base.lastIndexOf('.');
    if (dot >= 0) base = dot === 0 ? '' : base.slice(0, dot);
    base = base.replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^[._-]+|[._-]+$/g, '');
    const maxBase = 200 - extension.length;
    if (base.length > maxBase) base = base.slice(0, maxBase).replace(/[._-]+$/g, '');
    if (!base || base === 'blob') base = `piece-${sortOrder}`;
    return base + extension;
  }

  private static snapshotFileName(sortOrder: number): string {
    return `snapshot-${String(sortOrder).padStart(2, '0')}.html`;
  }

  private pieceCounts(entry: DemoPrototypeImport): PrototypeImport['pieceCounts'] {
    const counts = { html: 0, image: 0, snapshot: 0, markdown: 0 };
    for (const piece of entry.record.pieces) counts[piece.kind] += 1;
    return counts;
  }

  private toPrototypeSummary(entry: DemoPrototypeImport): PrototypeImportSummary {
    const record = entry.record;
    const counts = this.pieceCounts(entry);
    return {
      id: record.id,
      dossierRef: record.dossierRef,
      title: record.title,
      status: record.status,
      makerOrganisation: record.makerOrganisation,
      toolName: record.toolName,
      toolDetail: record.toolDetail,
      pieceCounts: counts,
      imagesExpected: record.imagesExpected,
      htmlExpected: record.htmlExpected,
      complete: counts.image >= record.imagesExpected && counts.html >= record.htmlExpected,
      hasPrompt: record.hasPrompt,
      warningCount: record.warnings.length,
      warningsAcknowledged: record.warningsAcknowledged,
      supersedesImportId: record.supersedesImportId,
      supersededByImportId: record.supersededByImportId,
      createdBy: record.createdBy,
      createdAt: record.createdAt,
    };
  }

  private refreshPrototypeRecord(id: string): PrototypeImport {
    const entry = this.prototypeImports.get(id);
    if (!entry) throw new Error(`import de démonstration inconnu : ${id}`);
    const summary = this.toPrototypeSummary(entry);
    entry.record = { ...entry.record, ...summary };
    return clone(entry.record);
  }

  private static piiWarnings(
    text: string,
    source: PrototypeWarning['source'],
    pieceRef: string | null
  ): PrototypeWarning[] {
    return scanPersonalData(scanForm(text)).map((hit) => ({
      code: hit.code,
      count: hit.count,
      example: hit.example,
      source,
      pieceRef,
      origin: 'server' as const,
    }));
  }

  private static inventoryStrings(screen: PrototypeScreen): string[] {
    const strings: string[] = [screen.label, ...screen.headings, ...screen.navigation, ...screen.actions];
    for (const form of screen.forms) {
      if (form.title) strings.push(form.title);
      if (form.submitLabel) strings.push(form.submitLabel);
      for (const field of form.fields) {
        for (const value of [field.label, field.name, field.type, field.placeholder, field.geodeField]) {
          if (value) strings.push(value);
        }
        strings.push(...field.options);
      }
    }
    for (const table of screen.tables) {
      if (table.title) strings.push(table.title);
      strings.push(...table.columns);
    }
    return strings;
  }

  /**
   * Empreinte de démonstration de la liste des avertissements à acquitter (forme seulement : FNV-1a 32 bits sur les
   * entrées triées, répété jusqu'à 64 hex). P1-b (web §4.1) : une empreinte qui change annule l'acquittement, comme
   * `storeWarnings` côté serveur ; sans entrée à acquitter, empreinte null et acquittement implicite.
   */
  private static ackFingerprint(record: PrototypeImport): string | null {
    const entries = record.warnings
      .filter((warning) => (PROTOTYPE_ACK_REQUIRED_CODES as readonly string[]).includes(warning.code))
      .map((warning) => `${warning.source}|${warning.pieceRef ?? ''}|${warning.code}|${warning.count}`)
      .sort();
    if (entries.length === 0) return null;
    const text = entries.join('|');
    let hex = '';
    for (let round = 0; hex.length < 64; round += 1) {
      let hash = 0x811c9dc5 ^ round;
      for (let i = 0; i < text.length; i += 1) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193) >>> 0;
      }
      hex += hash.toString(16).padStart(8, '0');
    }
    return hex.slice(0, 64);
  }

  private static recomputeAck(record: PrototypeImport): void {
    const fingerprint = FixtureRepository.ackFingerprint(record);
    if (fingerprint === null) {
      record.warningsFingerprint = null;
      record.warningsAcknowledgedBy = null;
      record.warningsAcknowledgedAt = null;
      record.warningsAcknowledged = true;
      return;
    }
    if (record.warningsFingerprint !== fingerprint) {
      // La liste a changé : l'acquittement tombe.
      record.warningsAcknowledgedBy = null;
      record.warningsAcknowledgedAt = null;
    }
    record.warningsFingerprint = fingerprint;
    record.warningsAcknowledged = record.warningsAcknowledgedBy !== null;
  }

  private async htmlWarnings(
    html: string,
    source: 'html' | 'snapshot',
    pieceRef: string
  ): Promise<PrototypeWarning[]> {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const { computePreviewWarnings } = await import('../prototypes/warnings');
    return computePreviewWarnings({ html: { doc, text: html, fallback: false }, prompt: null, images: [] })
      .filter((warning) => warning.source === 'html')
      .map((warning) => ({
        code: warning.code,
        count: warning.count,
        example: warning.example,
        source,
        pieceRef,
        origin: 'server' as const,
      }));
  }

  async listPrototypeImports(dossierRef: string): Promise<PrototypeImportSummary[]> {
    return [...this.prototypeImports.values()]
      .map((entry) => entry.record)
      .filter((record) => record.dossierRef === dossierRef)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .map((record) => this.toPrototypeSummary(this.prototypeImports.get(record.id)!));
  }

  async getPrototypeImport(id: string): Promise<PrototypeImport> {
    return this.refreshPrototypeRecord(id);
  }

  async createPrototypeImport(
    dossierRef: string,
    input: PrototypeImportInput,
    files: PrototypeUploadFiles
  ): Promise<PrototypeImport> {
    const id = this.newPieceId();
    const now = nowIso();
    const actor = this.options.currentUserName?.() ?? 'Démo';
    const prompt = input.promptText && input.promptText.trim() ? input.promptText : null;
    const pieces: PrototypePiece[] = [];
    const texts = new Map<string, string>();
    const images = new Map<string, Blob>();
    const warnings: PrototypeWarning[] = [];
    let sortOrder = 0;
    if (files.html) {
      const pieceId = this.newPieceId();
      const piece: PrototypePiece = {
        id: pieceId,
        kind: 'html',
        fileName: FixtureRepository.demoFileName(files.html.fileName, 'html', sortOrder),
        contentType: 'text/html',
        sizeBytes: files.html.text.length,
        sha256: FixtureRepository.demoSha(pieceId),
        width: null,
        height: null,
        label: null,
        role: null,
        sourceIndex: 0,
        sortOrder,
        createdAt: now,
      };
      pieces.push(piece);
      texts.set(pieceId, files.html.text);
      warnings.push(...await this.htmlWarnings(files.html.text, 'html', pieceId));
      sortOrder += 1;
    }
    // L2 : documents Markdown (rôle de `input.documents[i]`, sinon celui du fichier préparé).
    (files.markdowns ?? []).forEach((markdown, position) => {
      const pieceId = this.newPieceId();
      const role = input.documents?.[position]?.role ?? markdown.role;
      pieces.push({
        id: pieceId,
        kind: 'markdown',
        fileName: FixtureRepository.demoFileName(markdown.fileName, 'markdown', sortOrder),
        contentType: 'text/markdown',
        sizeBytes: markdown.bytes.length,
        sha256: FixtureRepository.demoSha(pieceId),
        width: null,
        height: null,
        label: null,
        role,
        sourceIndex: null,
        sortOrder,
        createdAt: now,
      });
      texts.set(pieceId, markdown.text);
      if (role === 'notes') {
        warnings.push(...FixtureRepository.piiWarnings(markdown.text, 'markdown', pieceId));
      }
      sortOrder += 1;
    });
    if (prompt) {
      warnings.push(...FixtureRepository.piiWarnings(prompt, 'prompt', null));
    }
    if (files.image) {
      const pieceId = this.newPieceId();
      const piece: PrototypePiece = {
        id: pieceId,
        kind: 'image',
        fileName: FixtureRepository.demoFileName(files.image.fileName, 'image', sortOrder),
        contentType: 'image/png',
        sizeBytes: files.image.png.size,
        sha256: FixtureRepository.demoSha(pieceId),
        width: files.image.width,
        height: files.image.height,
        label: null,
        role: null,
        sourceIndex: null,
        sortOrder,
        createdAt: now,
      };
      pieces.push(piece);
      images.set(pieceId, files.image.png);
      warnings.push({
        code: 'image.unscanned',
        count: 1,
        example: null,
        source: 'image',
        pieceRef: null,
        origin: 'server',
      });
    }
    for (const clientWarning of input.clientWarnings ?? []) {
      const piece = clientWarning.part === 'html'
        ? pieces.find((candidate) => candidate.kind === 'html')
        : pieces.find((candidate) => candidate.kind === 'image');
      if (piece) {
        warnings.push({
          code: clientWarning.code,
          count: clientWarning.count,
          example: clientWarning.example,
          source: clientWarning.part,
          pieceRef: piece.id,
          origin: 'client',
        });
      }
    }
    const derivations: PrototypeDerivationDeclaration[] = (input.derivedFrom ?? []).map((entry) => ({
      id: this.newPieceId(),
      moduleCode: entry.moduleCode,
      moduleVersion: '',
      ownerOrganisation: 'UpTK',
      relation: 'derivation' as const,
      declaredAt: now,
      withdrawnAt: null,
    }));
    const record: PrototypeImport = {
      id,
      dossierRef,
      title: input.title,
      status: 'imported',
      makerOrganisation: this.demoOrganisation(),
      toolName: input.toolName,
      toolDetail: input.toolDetail,
      pieceCounts: { html: 0, image: 0, snapshot: 0, markdown: 0 },
      imagesExpected: input.imageCount,
      htmlExpected: input.htmlCount ?? (files.html ? 1 : 0),
      complete: false,
      hasPrompt: prompt !== null,
      warningCount: 0,
      warningsAcknowledged: true,
      supersedesImportId: input.supersedesImportId,
      supersededByImportId: null,
      createdBy: actor,
      createdAt: now,
      pieces,
      promptSha256: prompt ? FixtureRepository.demoSha(id) : null,
      rights: {
        businessSpecsOwner: { organisation: this.demoOrganisation(), makerRef: 'demo' },
        generationOwner: 'geode',
        termsVersion: PROTOTYPE_RIGHTS_TERMS_VERSION,
        derivation: input.derivation,
        derivedFrom: derivations.map((declaration) => ({
          moduleCode: declaration.moduleCode,
          version: declaration.moduleVersion,
          ownerOrganisation: declaration.ownerOrganisation,
          ownership: 'other' as const,
        })),
      },
      rightsTextVersion: input.rightsTextVersion,
      derivations,
      attestation: { noRealData: true, rights: true, attestedBy: actor, attestedAt: now },
      inventory: null,
      warnings,
      warningsFingerprint: null,
      warningsAcknowledgedBy: null,
      warningsAcknowledgedAt: null,
    };
    FixtureRepository.recomputeAck(record);
    this.prototypeImports.set(id, { record, prompt, texts, images });
    if (input.supersedesImportId) {
      const predecessor = this.prototypeImports.get(input.supersedesImportId);
      if (predecessor) {
        predecessor.record.status = 'archived';
        predecessor.record.supersededByImportId = id;
      }
    }
    return this.refreshPrototypeRecord(id);
  }

  async addPrototypeImage(
    id: string,
    image: PreparedImage,
    input: PrototypeImageInput | null
  ): Promise<PrototypeImport> {
    const entry = this.prototypeImports.get(id);
    if (!entry) throw new Error(`import de démonstration inconnu : ${id}`);
    const counts = this.pieceCounts(entry);
    const pieceId = this.newPieceId();
    const sortOrder = entry.record.pieces.length;
    entry.record.pieces.push({
      id: pieceId,
      kind: 'image',
      fileName: FixtureRepository.demoFileName(image.fileName, 'image', sortOrder),
      contentType: 'image/png',
      sizeBytes: image.png.size,
      sha256: FixtureRepository.demoSha(pieceId),
      width: image.width,
      height: image.height,
      label: null,
      role: null,
      sourceIndex: null,
      sortOrder,
      createdAt: nowIso(),
    });
    entry.images.set(pieceId, image.png);
    entry.record.warnings = entry.record.warnings.filter(
      (warning) => !(warning.origin === 'server' && warning.source === 'image'));
    entry.record.warnings.push({
      code: 'image.unscanned',
      count: counts.image + 1,
      example: null,
      source: 'image',
      pieceRef: null,
      origin: 'server',
    });
    for (const clientWarning of input?.clientWarnings ?? []) {
      if (clientWarning.part === 'image') {
        entry.record.warnings.push({
          code: clientWarning.code,
          count: clientWarning.count,
          example: clientWarning.example,
          source: 'image',
          pieceRef: pieceId,
          origin: 'client',
        });
      }
    }
    FixtureRepository.recomputeAck(entry.record);
    return this.refreshPrototypeRecord(id);
  }

  /** L2 : imite POST …/sources (rest §4.4) : rang = HTML déjà reçus ; 409 sources_complete / archived. */
  async addPrototypeSource(
    id: string,
    html: PreparedHtml,
    input: PrototypeSourceInput | null
  ): Promise<PrototypeImport> {
    const entry = this.prototypeImports.get(id);
    if (!entry) throw FixtureRepository.httpError(404, 'prototype.not_found');
    if (entry.record.status === 'archived') throw FixtureRepository.httpError(409, 'prototype.archived');
    const received = entry.record.pieces.filter((piece) => piece.kind === 'html').length;
    if (received >= entry.record.htmlExpected) throw FixtureRepository.httpError(409, 'prototype.sources_complete');
    const pieceId = this.newPieceId();
    const sortOrder = entry.record.pieces.length;
    entry.record.pieces.push({
      id: pieceId,
      kind: 'html',
      fileName: FixtureRepository.demoFileName(html.fileName, 'html', sortOrder),
      contentType: 'text/html',
      sizeBytes: html.text.length,
      sha256: FixtureRepository.demoSha(pieceId),
      width: null,
      height: null,
      label: null,
      role: null,
      sourceIndex: received,
      sortOrder,
      createdAt: nowIso(),
    });
    entry.texts.set(pieceId, html.text);
    entry.record.warnings.push(...await this.htmlWarnings(html.text, 'html', pieceId));
    for (const clientWarning of input?.clientWarnings ?? []) {
      if (clientWarning.part === 'html') {
        entry.record.warnings.push({
          code: clientWarning.code,
          count: clientWarning.count,
          example: clientWarning.example,
          source: 'html',
          pieceRef: pieceId,
          origin: 'client',
        });
      }
    }
    FixtureRepository.recomputeAck(entry.record);
    return this.refreshPrototypeRecord(id);
  }

  private static httpError(status: number, code: string): Error {
    return Object.assign(new Error(code), { response: { status, data: { code } } });
  }

  async getPrototypePrompt(id: string): Promise<PrototypePrompt | null> {
    const entry = this.prototypeImports.get(id);
    if (!entry || entry.prompt === null) return null;
    return { importId: id, promptText: entry.prompt, promptSha256: entry.record.promptSha256 ?? '' };
  }

  async getPrototypePieceText(id: string, pieceId: string): Promise<string> {
    const entry = this.prototypeImports.get(id);
    const text = entry?.texts.get(pieceId);
    if (text === undefined) throw new Error(`pièce de démonstration inconnue : ${pieceId}`);
    return text;
  }

  async getPrototypePieceImage(id: string, pieceId: string): Promise<Blob> {
    const entry = this.prototypeImports.get(id);
    const image = entry?.images.get(pieceId);
    if (!image) throw new Error(`pièce de démonstration inconnue : ${pieceId}`);
    return new Blob([image], { type: 'image/png' });
  }

  async deletePrototypeSnapshot(id: string, pieceId: string): Promise<PrototypeImport> {
    const imp = await this.getPrototypeImport(id);
    if (!imp) throw new Error('prototype.not_found');
    const piece = imp.pieces.find(item => item.id === pieceId);
    if (!piece || piece.kind !== 'snapshot') throw new Error('prototype.snapshot_invalid');
    imp.pieces = imp.pieces.filter(item => item.id !== pieceId);
    if (imp.inventory) imp.inventory.screens = imp.inventory.screens.filter(screen => screen.sourcePieceRef !== pieceId);
    const entry = this.prototypeImports.get(id);
    if (entry) entry.record = imp;
    return imp;
  }
  async addPrototypeSnapshot(id: string, input: PrototypeSnapshotInput): Promise<PrototypeSnapshotResult> {
    const entry = this.prototypeImports.get(id);
    if (!entry) throw new Error(`import de démonstration inconnu : ${id}`);
    // L2 (rest §4.4) : `sourcePieceRef` obligatoire avec plusieurs sources, sinon une pièce html de l'import.
    const htmlPieces = entry.record.pieces.filter((candidate) => candidate.kind === 'html');
    let source: PrototypePiece | undefined;
    if (input.sourcePieceRef === undefined) {
      if (htmlPieces.length > 1) throw FixtureRepository.httpError(400, 'prototype.source_required');
      source = htmlPieces[0];
    } else {
      source = htmlPieces.find((candidate) => candidate.id === input.sourcePieceRef);
      if (!source) throw FixtureRepository.httpError(400, 'prototype.source_invalid');
    }
    const pieceId = this.newPieceId();
    const sortOrder = entry.record.pieces.length;
    const piece: PrototypePiece = {
      id: pieceId,
      kind: 'snapshot',
      fileName: FixtureRepository.snapshotFileName(sortOrder),
      contentType: 'text/html',
      sizeBytes: input.html.length,
      sha256: FixtureRepository.demoSha(pieceId),
      width: null,
      height: null,
      label: input.label,
      role: null,
      sourceIndex: source?.sourceIndex ?? 0,
      sortOrder,
      createdAt: nowIso(),
    };
    entry.record.pieces.push(piece);
    entry.texts.set(pieceId, input.html);
    entry.record.warnings.push(...await this.htmlWarnings(input.html, 'snapshot', pieceId));
    FixtureRepository.recomputeAck(entry.record);
    return { piece: clone(piece), import: this.refreshPrototypeRecord(id) };
  }

  /**
   * UPT-2079 L14a (rest §4.2 et §4.3) : contrôles de l'inventaire v2 imités du serveur — sélecteurs de section au motif et UNIQUES dans l'écran, section cachée sans
   * libellé (forcé), bornes (40 sections, 40 images), résumé de style (couleurs `#rrggbb`, parts 0..1 de somme ≤ 1,001, bornes des listes). Violation → 400
   * `prototype.inventory_invalid`. Le résumé est une donnée non fiable : il ne choisit qu'entre des valeurs bornées.
   */
  private static checkInventoryV2(inventory: PrototypeInventory): PrototypeInventory {
    const invalid = () => FixtureRepository.httpError(400, 'prototype.inventory_invalid');
    const hex = /^#[0-9a-f]{6}$/;
    const checkList = (list: unknown, max: number): void => {
      if (!Array.isArray(list) || list.length > max) throw invalid();
      let sum = 0;
      for (const entry of list as { color?: unknown; weight?: unknown }[]) {
        if (typeof entry?.color !== 'string' || !hex.test(entry.color)) throw invalid();
        if (typeof entry.weight !== 'number' || !(entry.weight >= 0 && entry.weight <= 1)) throw invalid();
        sum += entry.weight;
      }
      if (sum > 1.001) throw invalid();
    };
    const stored = clone(inventory);
    for (const source of stored.sources ?? []) {
      const style = source.style;
      if (style == null) continue;
      checkList(style.backgrounds, PROTOTYPE_STYLE_LIMITS.backgrounds);
      checkList(style.texts, PROTOTYPE_STYLE_LIMITS.texts);
      checkList(style.accents, PROTOTYPE_STYLE_LIMITS.accents);
      checkList(style.borders, PROTOTYPE_STYLE_LIMITS.borders);
      if (style.cards.background !== null && !hex.test(style.cards.background)) throw invalid();
    }
    for (const screen of stored.screens) {
      const sections = screen.sections ?? [];
      if (sections.length > PROTOTYPE_INVENTORY_LIMITS.sectionsPerScreen || (screen.images ?? []).length > PROTOTYPE_INVENTORY_LIMITS.imagesPerScreen) {
        throw FixtureRepository.httpError(400, 'prototype.inventory_bounds');
      }
      const seen = new Set<string>();
      for (const section of sections) {
        if (!MODULE_SPEC_SECTION_SELECTOR.test(section.selector) || seen.has(section.selector)) throw invalid();
        seen.add(section.selector);
        if (section.hidden) section.label = null;
      }
    }
    return stored;
  }

  async putPrototypeInventory(id: string, inventory: PrototypeInventory): Promise<PrototypeImport> {
    const entry = this.prototypeImports.get(id);
    if (!entry) throw new Error(`import de démonstration inconnu : ${id}`);
    if (inventory.version === 2) inventory = FixtureRepository.checkInventoryV2(inventory);
    const htmlPieces = entry.record.pieces
      .filter((piece) => piece.kind === 'html')
      .sort((a, b) => (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0));
    const stored = clone(inventory);
    if (stored.sources === undefined) {
      // L2 : un seul HTML → le serveur complète ; plusieurs → `sources` obligatoire.
      if (htmlPieces.length > 1) throw FixtureRepository.httpError(400, 'prototype.inventory_invalid');
      stored.sources = htmlPieces.map((piece) => ({
        sourceIndex: piece.sourceIndex ?? 0,
        pieceRef: piece.id,
        title: stored.title,
        lang: stored.lang,
      }));
    }
    entry.record.inventory = stored;
    entry.record.warnings = entry.record.warnings.filter(
      (warning) => !(warning.origin === 'server' && warning.source === 'inventory'));
    for (const screen of inventory.screens) {
      entry.record.warnings.push(
        ...FixtureRepository.piiWarnings(
          FixtureRepository.inventoryStrings(screen).join('\n'),
          'inventory',
          screen.sourcePieceRef
        )
      );
    }
    FixtureRepository.recomputeAck(entry.record);
    return this.refreshPrototypeRecord(id);
  }

  async getPrototypeInventory(id: string): Promise<PrototypeInventory> {
    const entry = this.prototypeImports.get(id);
    if (!entry?.record.inventory) {
      throw Object.assign(new Error('prototype.inventory_absent'), {
        response: { status: 404, data: { code: 'prototype.inventory_absent' } },
      });
    }
    return clone(entry.record.inventory);
  }

  async patchPrototypeImport(id: string, patch: PrototypeImportPatch): Promise<PrototypeImport> {
    const entry = this.prototypeImports.get(id);
    if (!entry) throw new Error(`import de démonstration inconnu : ${id}`);
    if (patch.title !== undefined) entry.record.title = patch.title;
    if (patch.toolDetail !== undefined) entry.record.toolDetail = patch.toolDetail;
    return this.refreshPrototypeRecord(id);
  }

  private readonly prototypeRetirements = new Map<string, { status: PrototypeImport['status']; deadline: string | null }>();

  async prototypeRemovalImpact(id: string) {
    const record = await this.getPrototypeImport(id);
    return { id, title: record.title, pieces: record.pieces.length, analyses: 0, versions: [] as number[], protectedSource: false, simulations: 0, captures: record.pieceCounts.snapshot, purgeAfter: this.prototypeRetirements.get(id)?.deadline ?? null, history: [] };
  }
  async deletePrototypeImport(id: string): Promise<void> {
    const entry = this.prototypeImports.get(id);
    if (entry && entry.record.status !== 'retired') {
      this.prototypeRetirements.set(id, { status: entry.record.status, deadline: new Date(Date.now() + 7 * 86400000).toISOString() });
      entry.record.status = 'retired';
    }
  }
  async hidePrototypeImport(id: string): Promise<void> {
    await this.deletePrototypeImport(id);
    const retired = this.prototypeRetirements.get(id);
    if (retired) retired.deadline = null;
  }
  async restorePrototypeImport(id: string): Promise<void> {
    const entry = this.prototypeImports.get(id);
    const retired = this.prototypeRetirements.get(id);
    if (retired?.deadline && Date.now() >= Date.parse(retired.deadline)) {
      throw Object.assign(new Error('prototype.restore_expired'), { response: { status: 409, data: { code: 'prototype.restore_expired' } } });
    }
    if (entry?.record.status === 'retired' && retired) {
      entry.record.status = retired.status;
      this.prototypeRetirements.delete(id);
    }
  }

  /* ── Analyse des prototypes (UPT-2096 P1-b, web §4.1) ───────────────── */

  /** Règle du serveur (rest §4.3) : empreinte reçue ≠ courante, y compris courante null → 409 prototype.warnings_changed. */
  async acknowledgePrototypeWarnings(id: string, input: PrototypeAcknowledgeInput): Promise<PrototypeImport> {
    const entry = this.prototypeImports.get(id);
    if (!entry) {
      throw Object.assign(new Error('prototype.not_found'), {
        response: { status: 404, data: { code: 'prototype.not_found' } },
      });
    }
    if (entry.record.status === 'archived') {
      throw Object.assign(new Error('prototype.archived'), {
        response: { status: 409, data: { code: 'prototype.archived' } },
      });
    }
    const current = entry.record.warningsFingerprint;
    if (current === null || current !== input.warningsFingerprint) {
      throw Object.assign(new Error('prototype.warnings_changed'), {
        response: { status: 409, data: { code: 'prototype.warnings_changed' } },
      });
    }
    entry.record.warningsAcknowledgedBy = 'demo';
    entry.record.warningsAcknowledgedAt = nowIso();
    entry.record.warningsAcknowledged = true;
    return this.refreshPrototypeRecord(id);
  }

  getPrototypeAnalysisEstimate(_id: string): Promise<PrototypeAnalysisEstimate> {
    return Promise.reject(new NotServedError('prototypeAnalysis', 'getPrototypeAnalysisEstimate'));
  }

  getAiRoute(): Promise<AiRoute> {
    return Promise.reject(new NotServedError('prototypeAnalysis', 'getAiRoute'));
  }

  startPrototypeAnalysis(_id: string, _input: PrototypeAnalysisInput): Promise<PrototypeAnalysis> {
    return Promise.reject(new NotServedError('prototypeAnalysis', 'startPrototypeAnalysis'));
  }

  listPrototypeAnalyses(_id: string): Promise<PrototypeAnalysisSummary[]> {
    return Promise.reject(new NotServedError('prototypeAnalysis', 'listPrototypeAnalyses'));
  }

  getPrototypeAnalysis(_id: string, _analysisId: string): Promise<PrototypeAnalysis> {
    return Promise.reject(new NotServedError('prototypeAnalysis', 'getPrototypeAnalysis'));
  }

  applyPrototypeAnalysis(
    _id: string,
    _analysisId: string,
    _input: PrototypeAnalysisApplyInput
  ): Promise<PrototypeAnalysisApplyResult> {
    return Promise.reject(new NotServedError('prototypeAnalysis', 'applyPrototypeAnalysis'));
  }

  rejectPrototypeAnalysis(
    _id: string,
    _analysisId: string,
    _input: PrototypeAnalysisRejectInput
  ): Promise<PrototypeAnalysis> {
    return Promise.reject(new NotServedError('prototypeAnalysis', 'rejectPrototypeAnalysis'));
  }
}


let shared: FixtureRepository | null = null;

/** Instance partagée par les écrans (une session = un magasin). */
export const getSharedFixtureRepository = (
  options: FixtureRepositoryOptions = {}
): FixtureRepository => {
  if (!shared) {
    shared = new FixtureRepository(options);
  }
  return shared;
};

/** Tests : réinitialise l'instance partagée. */
export const resetSharedFixtureRepository = (): void => {
  shared = null;
};

/* ── Import des dossiers de démonstration (web §5.1) ─────────────────────── */

/** Dossiers présents dans le stockage de démonstration (`[]` à défaut ; lecture en try/catch). */
export const readDemoDossiers = (): DesignDossier[] => readStorage()?.dossiers ?? [];

/**
 * Réécrit l'état de démonstration SANS dossiers, specs ni versions ; plans,
 * exécutions et imports de prototypes de démonstration sont conservés (décision
 * CORE du 12.10). L'instance partagée en mémoire est alignée pour qu'une
 * écriture ultérieure (plan, exécution) ne réintroduise pas les dossiers.
 */
export const clearDemoStorage = (): void => {
  const stored = readStorage();
  if (stored) {
    writeStorage({ ...stored, dossiers: [], specs: [], specVersions: {}, moduleSpecVersions: {}, moduleSpecDrafts: {}, previewDatasets: {}, generationRuns: seedGenerationRuns() });
  }
  shared?.dropDossiersSpecsAndVersions();
};
